var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChatBubbleIcon, CheckCircledIcon, ChevronDownIcon, ChevronRightIcon, ClockIcon, CrossCircledIcon, ListBulletIcon, MixerHorizontalIcon, PersonIcon } from "@radix-ui/react-icons";
import { useAuth, useCart, useCheckout, useOrderIntent, useServiceTypes, useUI } from "../../providers";
import { RadixAccordion, RadixAccordionContent, RadixAccordionHeader, RadixAccordionItem, RadixAccordionTrigger } from "../primitives/RadixAccordion";
import { RadixCheckbox } from "../primitives/RadixCheckbox";
import { RadixInput } from "../primitives/RadixInput";
import { RadixPhoneInput } from "../primitives/RadixPhoneInput";
import { RadixToggleGroup } from "../primitives/RadixToggleGroup";
import { RadixRadioGroup } from "../primitives/RadixRadioGroup";
import { RadixTabs, RadixTabsContent, RadixTabsList, RadixTabsTrigger } from "../primitives/RadixTabs";
import { OrderSummary } from "../orderSummary";
import { CreditPaymentContextProvider } from "../creditPayment";
import { PaymentErrorNotice } from "../creditPayment/PaymentErrorNotice";
import { useCreditPaymentContext } from "../creditPayment";
import { AVSCollectionDetails } from "../avsCollection";
import { StoreMapEmbed } from "../storeMapEmbed";
import { formatCurrency } from "../utility/formatCurrency";
import { useDebouncedValidation } from "../utility/useDebouncedValidation";
import { RadixButton } from "../primitives/RadixButton";
import { RadixLoading } from "../primitives/RadixLoading";
import { RadixModal } from "../primitives/RadixModal";
import { extractApiErrorMessage } from "../../http/extractApiErrorMessage";
import { StateInitializationLoading } from "../stateInitializationLoading";
import { OrderTimeSelectionFields } from "../orderTimeSelectionFlyOut";
import { OrderAddressDetailsFlyOut } from "../orderAddressDetailsFlyOut";
import { DiscountsSection } from "../discountsSection";
import { getPendingDealCode } from "../dealsSelection";
import { UserAccountManagementFlyOut } from "../userAccountManagementFlyOut";
import { SmsMarketingOptIn } from "../smsMarketingOptIn/SmsMarketingOptIn";
import { MarketingDisplay } from "../marketingDisplay";
import { guestDetailsSchema, verticalCheckoutSchema } from "./verticalCheckoutSchema";
import { ApplePayGlyph, CashGlyph, CreditCardGlyph, DeliveryServiceIcon, EditIconCheckbox, GooglePayIcon, GratuityIcon, StoreIcon } from "../icons";
import GoogleAnalytics from "../../analytics/google";
import AnalyticsManager from "../../functions/analytics";
import PersistenceManager from "../../functions/persistence";
import "../ui.css";
import "./verticalCheckout.css";
import { formatter } from "../utility/formatter";
import { ValidationMethods } from "../utility/validationMethods";
import { getXplorPayDebitFeeDescriptor } from "../utility/xplorPayDebitFeeDescriptor";
var FALLBACK_TIP_PERCENTAGES = [15, 18, 20, 25];
var VERTICAL_CHECKOUT_BODY_CLASS = "vertical-checkout-active";
var PAYMENT_SELECTION_REQUIREMENTS_MESSAGE = "Complete service type, timing, address, and guest details before selecting payment.";
var toNumber = function (value) {
    if (value === null || value === undefined || value === "") {
        return 0;
    }
    var numericValue = typeof value === "string" ? Number(value) : value;
    return Number.isNaN(numericValue) ? 0 : numericValue;
};
var toBoolean = function (value) {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "number") {
        return value === 1;
    }
    if (typeof value === "string") {
        var normalizedValue = value.trim().toLowerCase();
        return normalizedValue === "1" || normalizedValue === "true";
    }
    return false;
};
var toServiceTypeOption = function (id, name, disabled) {
    if (disabled === void 0) { disabled = false; }
    return ({
        value: String(id),
        label: name,
        ariaLabel: "Select ".concat(name),
        disabled: disabled,
    });
};
var getSessionStore = function () {
    var _a;
    var sessionStore = (_a = window.session) === null || _a === void 0 ? void 0 : _a.store;
    if (!sessionStore || typeof sessionStore !== "object") {
        return null;
    }
    return sessionStore;
};
var shouldAutoCheckRewards = function () {
    var _a, _b;
    if (typeof window === "undefined") {
        return false;
    }
    var session = window.session;
    var sessionValue = (_b = (_a = session === null || session === void 0 ? void 0 : session.store) === null || _a === void 0 ? void 0 : _a.auto_check_rewards) !== null && _b !== void 0 ? _b : session === null || session === void 0 ? void 0 : session.auto_check_rewards;
    if (typeof sessionValue === "boolean") {
        return sessionValue;
    }
    if (typeof sessionValue === "number") {
        return sessionValue === 1;
    }
    if (typeof sessionValue === "string") {
        var normalizedValue = sessionValue.trim().toLowerCase();
        return normalizedValue === "1" || normalizedValue === "true";
    }
    return false;
};
var getAddressLine = function (store) {
    if (!store) {
        return "";
    }
    var segments = [store.city, store.state, store.zip].filter(function (segment) {
        return Boolean(String(segment || "").trim());
    });
    return segments.join(", ");
};
var formatDeliveryAddress = function (address) {
    if (!address) {
        return "";
    }
    var lineOne = String(address.address || "").trim();
    var lineTwo = String(address.address2 || "").trim();
    var city = String(address.city || "").trim();
    var state = String(address.state || "").trim();
    var zip = String(address.zip || "").trim();
    var streetSegment = [lineOne, lineTwo].filter(function (segment) { return segment.length > 0; }).join(" ").trim();
    var cityStateZip = [city, state, zip].filter(function (segment) { return segment.length > 0; }).join(", ").trim();
    return [streetSegment, cityStateZip].filter(function (segment) { return segment.length > 0; }).join(", ").trim();
};
var getFormattedDeliveryAddressLines = function (address) {
    if (!address) {
        return {
            lineOne: "",
            lineTwo: "",
        };
    }
    var lineOne = [String(address.address || "").trim(), String(address.address2 || "").trim()]
        .filter(function (segment) { return segment.length > 0; })
        .join(" ")
        .trim();
    var lineTwo = [String(address.city || "").trim(), String(address.state || "").trim(), String(address.zip || "").trim()]
        .filter(function (segment) { return segment.length > 0; })
        .join(", ")
        .trim();
    return {
        lineOne: lineOne,
        lineTwo: lineTwo,
    };
};
var getTipPercentages = function (tipOptions) {
    if (!tipOptions) {
        return FALLBACK_TIP_PERCENTAGES;
    }
    var segments = tipOptions.split("|");
    var nextPercentages = [];
    for (var _i = 0, segments_1 = segments; _i < segments_1.length; _i++) {
        var segment = segments_1[_i];
        var trimmed = String(segment || "").trim();
        if (!trimmed) {
            continue;
        }
        var parsed = Number(trimmed);
        if (!Number.isFinite(parsed) || parsed <= 0) {
            continue;
        }
        nextPercentages.push(parsed);
    }
    if (nextPercentages.length === 0) {
        return FALLBACK_TIP_PERCENTAGES;
    }
    return nextPercentages;
};
var getItemModifiers = function (modifiers) {
    if (!Array.isArray(modifiers)) {
        return [];
    }
    return modifiers.filter(function (modifier) {
        if (!modifier || typeof modifier !== "object") {
            return false;
        }
        var quantity = typeof modifier.quantity === "number" ? modifier.quantity : 0;
        var hasName = Boolean(String(modifier.name || "").trim());
        return quantity > 0 && hasName;
    });
};
var getModifiersByPortion = function (modifiers, portions) {
    return getItemModifiers(modifiers).filter(function (modifier) {
        return Boolean(modifier.portion && portions.includes(modifier.portion));
    });
};
var formatModifiers = function (modifiers) {
    return modifiers.map(function (modifier) { return formatModifier(modifier); }).join(", ");
};
var getLineComment = function (comment) {
    return typeof comment === "string" ? comment.trim() : "";
};
var isSpecialDiscountLine = function (line) {
    var categoryId = Number(line.category_id);
    if (Number.isFinite(categoryId) && categoryId === 0) {
        return true;
    }
    var lineType = line.line_type;
    return typeof lineType === "string" && lineType.toLowerCase() === "special_discount";
};
var formatModifier = function (modifier) {
    var baseName = String(modifier.name || "Modifier");
    var quantity = typeof modifier.quantity === "number" ? modifier.quantity : 0;
    if (quantity <= 1) {
        return formatter.removeTrailingAsterisk(baseName);
    }
    return "".concat(formatter.removeTrailingAsterisk(baseName), " x").concat(quantity);
};
var isDeliveryServiceType = function (candidate) {
    if (!candidate) {
        return false;
    }
    if (typeof candidate.has_delivery !== "undefined" && candidate.has_delivery !== null) {
        return Boolean(Number(candidate.has_delivery));
    }
    return Number(candidate.pos_id) === 1;
};
var hasAddressFields = function (candidate) {
    if (!candidate) {
        return false;
    }
    var address = String(candidate.address || "").trim();
    var city = String(candidate.city || "").trim();
    var state = String(candidate.state || "").trim();
    var zip = String(candidate.zip || "").trim();
    return Boolean(address && city && state && zip);
};
var parseMinimumDeliveryAmount = function (value) {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string" && value.trim().length > 0) {
        var parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return 0;
};
var isAppleDevice = function () {
    var userAgent = window.navigator.userAgent.toLowerCase();
    return userAgent.includes("iphone") || userAgent.includes("ipad") || userAgent.includes("macintosh");
};
var isAndroidDevice = function () {
    var userAgent = window.navigator.userAgent.toLowerCase();
    return userAgent.includes("android");
};
var hasDisplayValue = function (value) {
    return typeof value === "string" && value.trim().length > 0;
};
var formatScheduledDatetimeDisplay = function (value) {
    var _a, _b;
    var normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
        return "Not selected";
    }
    var standardFormatMatch = normalizedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s(\d{1,2}):(\d{2})\s(AM|PM)$/i);
    if (standardFormatMatch) {
        var month = standardFormatMatch[1], day = standardFormatMatch[2], year = standardFormatMatch[3], hour = standardFormatMatch[4], minute = standardFormatMatch[5], meridiem = standardFormatMatch[6];
        return "".concat(month.padStart(2, "0"), "/").concat(day.padStart(2, "0"), "/").concat(year, " ").concat(hour.padStart(2, "0"), ":").concat(minute, " ").concat(meridiem.toUpperCase());
    }
    var dbFormatMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2})(?::\d{2})?$/);
    if (dbFormatMatch) {
        var year = dbFormatMatch[1], month = dbFormatMatch[2], day = dbFormatMatch[3], hourText = dbFormatMatch[4], minute = dbFormatMatch[5];
        var hour24 = Number(hourText);
        var meridiem = hour24 >= 12 ? "PM" : "AM";
        var hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
        return "".concat(month, "/").concat(day, "/").concat(year, " ").concat(String(hour12).padStart(2, ""), ":").concat(minute, " ").concat(meridiem);
    }
    var parsedDate = new Date(normalizedValue);
    if (!Number.isNaN(parsedDate.getTime())) {
        var storeTimezone = String(((_b = (_a = window.session) === null || _a === void 0 ? void 0 : _a.store) === null || _b === void 0 ? void 0 : _b.timezone) || "UTC");
        var formatter_1 = new Intl.DateTimeFormat("en-US", {
            timeZone: storeTimezone,
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
        var formatted = formatter_1.format(parsedDate);
        var _c = formatted.split(", "), _d = _c[0], datePart = _d === void 0 ? "" : _d, _e = _c[1], timePart = _e === void 0 ? "" : _e;
        if (datePart && timePart) {
            return "".concat(datePart, " ").concat(timePart);
        }
    }
    return normalizedValue;
};
var isWalletSupportEnabled = function (value) {
    if (typeof value === "undefined" || value === null || value === "") {
        return true;
    }
    return Boolean(Number(value));
};
var getAppliedDiscountCodes = function (cartRecord) {
    if (!cartRecord) {
        return [];
    }
    var rawDiscounts = cartRecord.discounts;
    var discountList = [];
    if (Array.isArray(rawDiscounts)) {
        discountList = rawDiscounts;
    }
    if (typeof rawDiscounts === "string") {
        try {
            var parsedDiscounts = JSON.parse(rawDiscounts);
            if (Array.isArray(parsedDiscounts)) {
                discountList = parsedDiscounts;
            }
        }
        catch (_error) {
            discountList = [];
        }
    }
    return discountList
        .map(function (discount) {
        if (!discount || typeof discount !== "object") {
            return "";
        }
        return String(discount.code || "").trim();
    })
        .filter(function (code) { return code.length > 0; });
};
var getFutureOrderDays = function () {
    var _a, _b, _c, _d;
    var sessionValue = (_c = (_b = (_a = window.session) === null || _a === void 0 ? void 0 : _a.store) === null || _b === void 0 ? void 0 : _b.future_order_days) !== null && _c !== void 0 ? _c : (_d = window.session) === null || _d === void 0 ? void 0 : _d.future_order_days;
    if (typeof sessionValue === "number" && Number.isFinite(sessionValue)) {
        return sessionValue;
    }
    if (typeof sessionValue === "string" && sessionValue.trim().length > 0) {
        var parsed = Number(sessionValue);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return 0;
};
var getAddressCreationCandidate = function (address) {
    if (!address || typeof address !== "object") {
        return null;
    }
    var candidate = {
        address: String(address.address || "").trim(),
        address2: String(address.address2 || "").trim(),
        city: String(address.city || "").trim(),
        state: String(address.state || "").trim(),
        zip: String(address.zip || "").trim(),
    };
    if (!candidate.address || !candidate.city || !candidate.state || !candidate.zip) {
        return null;
    }
    return candidate;
};
var getAddressHistoryMatch = function (addresses, candidate) {
    if (!Array.isArray(addresses) || addresses.length === 0) {
        return null;
    }
    var normalize = function (value) { return String(value || "").trim().toLowerCase(); };
    return addresses.find(function (address) {
        return normalize(address.address) === normalize(candidate.address)
            && normalize(address.address2) === normalize(candidate.address2)
            && normalize(address.city) === normalize(candidate.city)
            && normalize(address.state) === normalize(candidate.state)
            && normalize(address.zip) === normalize(candidate.zip);
    }) || null;
};
var PaymentOptionsWithSavedCards = function (_a) {
    var _b;
    var canSelectPayment = _a.canSelectPayment, creditPaymentsEnabled = _a.creditPaymentsEnabled, paymentType = _a.paymentType, setPaymentType = _a.setPaymentType, paymentOptions = _a.paymentOptions, payLaterTabLabel = _a.payLaterTabLabel, onFinalizeCashOrder = _a.onFinalizeCashOrder, isSubmittingCashOrder = _a.isSubmittingCashOrder;
    var _c = useCreditPaymentContext(), savedCards = _c.savedCards, selectedSavedCard = _c.selectedSavedCard, setSelectedSavedCard = _c.setSelectedSavedCard, processorInfo = _c.processorInfo, payWithSavedCard = _c.payWithSavedCard, savedPaymentError = _c.savedPaymentError, clearSavedPaymentError = _c.clearSavedPaymentError, isSavedPaymentSubmitting = _c.isSavedPaymentSubmitting, isAvsValid = _c.isAvsValid;
    var _d = useUI(), storeSupportsApplePay = _d.storeSupportsApplePay, storeSupportsGooglePay = _d.storeSupportsGooglePay;
    var hasUnifiedWalletForm = (processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.processor) === "xplorpay"
        && (storeSupportsApplePay || storeSupportsGooglePay);
    var sessionStore = ((_b = window.session) === null || _b === void 0 ? void 0 : _b.store) || {};
    var appleDevice = useMemo(function () { return isAppleDevice(); }, []);
    var androidDevice = useMemo(function () { return isAndroidDevice(); }, []);
    var matchingCards = useMemo(function () {
        var processorKey = typeof (processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.processor) === "string" ? processorInfo.processor : null;
        if (!processorKey) {
            return savedCards;
        }
        return savedCards.filter(function (card) { return card.credit_processor === processorKey; });
    }, [processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.processor, savedCards]);
    var digitalWalletAvailability = useMemo(function () {
        if (!creditPaymentsEnabled || (processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.processor) === "xplorpay") {
            return {
                applePay: false,
                googlePay: false,
            };
        }
        var supportsApplePay = isWalletSupportEnabled(sessionStore.supports_applepay);
        var supportsGooglePay = isWalletSupportEnabled(sessionStore.supports_googlepay);
        var checkoutWindow = window;
        var applePayAvailable = Boolean(checkoutWindow.ApplePaySession
            && typeof checkoutWindow.ApplePaySession.canMakePayments === "function"
            && checkoutWindow.ApplePaySession.canMakePayments());
        var tokenKey = hasDisplayValue(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.token_key) ? processorInfo.token_key : "";
        var appleMerchantName = hasDisplayValue(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.applepay_wallet_merchant_name)
            ? processorInfo.applepay_wallet_merchant_name
            : "";
        var appleMerchantId = hasDisplayValue(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.applepay_wallet_mid)
            ? processorInfo.applepay_wallet_mid
            : "";
        var appleButtonId = hasDisplayValue(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.applepay_button_id)
            ? processorInfo.applepay_button_id
            : "";
        var hasApplePayConfig = tokenKey.length > 0
            && appleMerchantName.length > 0
            && appleMerchantId.length > 0
            && appleButtonId.length > 0;
        var googleMerchantName = hasDisplayValue(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.googlepay_wallet_merchant_name)
            ? processorInfo.googlepay_wallet_merchant_name
            : "";
        var googleMerchantId = hasDisplayValue(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.googlepay_wallet_mid)
            ? processorInfo.googlepay_wallet_mid
            : "";
        var googleButtonId = hasDisplayValue(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.googlepay_button_id)
            ? processorInfo.googlepay_button_id
            : "";
        var hasGooglePayConfig = tokenKey.length > 0
            && googleMerchantName.length > 0
            && googleMerchantId.length > 0
            && googleButtonId.length > 0;
        var eligibleApplePay = appleDevice && supportsApplePay && applePayAvailable && hasApplePayConfig;
        var eligibleGooglePay = !eligibleApplePay && supportsGooglePay && hasGooglePayConfig;
        return {
            applePay: eligibleApplePay,
            googlePay: eligibleGooglePay,
        };
    }, [androidDevice, appleDevice, creditPaymentsEnabled, processorInfo]);
    var canRenderApplePayOption = digitalWalletAvailability.applePay;
    var canRenderGooglePayOption = digitalWalletAvailability.googlePay;
    var canRenderDigitalWallet = canRenderApplePayOption || canRenderGooglePayOption;
    useEffect(function () {
        if (creditPaymentsEnabled) {
            return;
        }
        if (paymentType === "credit"
            || paymentType === "apple_pay"
            || paymentType === "google_pay"
            || paymentType.startsWith("saved-")) {
            setSelectedSavedCard(null);
            setPaymentType("none");
            clearSavedPaymentError();
        }
    }, [clearSavedPaymentError, creditPaymentsEnabled, paymentType, setPaymentType, setSelectedSavedCard]);
    var cashOption = useMemo(function () {
        return paymentOptions.find(function (option) { return option.value === "cash"; }) || null;
    }, [paymentOptions]);
    var creditOption = useMemo(function () {
        return paymentOptions.find(function (option) { return option.value === "credit"; }) || null;
    }, [paymentOptions]);
    var optionsWithSavedCards = useMemo(function () {
        var options = [];
        if (creditPaymentsEnabled) {
            if (canRenderApplePayOption) {
                options.push({
                    value: "apple_pay",
                    label: (React.createElement("span", { className: "vertical-checkout-payment-label-wrap" },
                        React.createElement("span", { className: "vertical-checkout-payment-icon vertical-checkout-payment-icon--wallet", "aria-hidden": "true" },
                            React.createElement(ApplePayGlyph, null)),
                        React.createElement("span", null, "Apple Pay"))),
                    description: "Pay with Apple Pay.",
                    ariaLabel: "Pay with Apple Pay",
                });
            }
            if (canRenderGooglePayOption) {
                options.push({
                    value: "google_pay",
                    label: (React.createElement("span", { className: "vertical-checkout-payment-label-wrap" },
                        React.createElement("span", { className: "vertical-checkout-payment-icon vertical-checkout-payment-icon--wallet", "aria-hidden": "true" },
                            React.createElement(GooglePayIcon, null)),
                        React.createElement("span", null, "Google Pay"))),
                    description: "Pay with Google Pay.",
                    ariaLabel: "Pay with Google Pay",
                });
            }
            matchingCards.forEach(function (card) {
                options.push({
                    value: "saved-".concat(card.id),
                    label: (React.createElement("span", { className: "vertical-checkout-payment-label-wrap" },
                        React.createElement("span", { className: "vertical-checkout-payment-icon", "aria-hidden": "true" },
                            React.createElement(CreditCardGlyph, null)),
                        React.createElement("span", null,
                            "Pay with ",
                            card.friendly_name))),
                    description: "Use your saved payment method.",
                    ariaLabel: "Pay with ".concat(card.friendly_name),
                });
            });
        }
        if (creditOption) {
            var hasSavedCards = matchingCards.length > 0;
            var creditLabel = hasUnifiedWalletForm
                ? "Card or digital wallet"
                : hasSavedCards ? "Add card" : "Pay with Card";
            options.push(__assign(__assign({}, creditOption), { label: (React.createElement("span", { className: "vertical-checkout-payment-label-wrap" },
                    React.createElement("span", { className: "vertical-checkout-payment-icon", "aria-hidden": "true" },
                        React.createElement(CreditCardGlyph, null)),
                    React.createElement("span", null, creditLabel))), description: hasUnifiedWalletForm
                    ? "Use a credit or debit card, or an available digital wallet."
                    : creditOption.description, ariaLabel: creditLabel }));
        }
        return options.map(function (option) { return (__assign(__assign({}, option), { disabled: !canSelectPayment })); });
    }, [canRenderApplePayOption, canRenderGooglePayOption, canSelectPayment, creditOption, creditPaymentsEnabled, hasUnifiedWalletForm, matchingCards]);
    var cashOnlyOptions = useMemo(function () {
        if (!cashOption) {
            return [];
        }
        return [__assign(__assign({}, cashOption), { disabled: !canSelectPayment })];
    }, [canSelectPayment, cashOption]);
    var canShowPayNowTab = optionsWithSavedCards.length > 0 || canRenderDigitalWallet;
    var canShowPayLaterTab = cashOnlyOptions.length > 0;
    var hasBothPaymentTabs = canShowPayNowTab && canShowPayLaterTab;
    var _e = useState(function () {
        if (!canShowPayNowTab && canShowPayLaterTab) {
            return "pay-later";
        }
        if (paymentType === "cash" && canShowPayLaterTab) {
            return "pay-later";
        }
        return "pay-now";
    }), selectedPaymentTab = _e[0], setSelectedPaymentTab = _e[1];
    useEffect(function () {
        if (!canShowPayNowTab && canShowPayLaterTab) {
            setSelectedPaymentTab("pay-later");
            return;
        }
        if (!canShowPayLaterTab) {
            setSelectedPaymentTab("pay-now");
            return;
        }
        if (paymentType === "cash") {
            setSelectedPaymentTab("pay-later");
            return;
        }
        setSelectedPaymentTab("pay-now");
    }, [canShowPayLaterTab, canShowPayNowTab, paymentType]);
    var selectedPaymentValue = paymentType === "none" ? null : paymentType;
    var handlePaymentChange = function (nextPaymentType) {
        if (nextPaymentType.startsWith("saved-")) {
            var nextSavedCard = matchingCards.find(function (card) { return "saved-".concat(card.id) === nextPaymentType; }) || null;
            setSelectedSavedCard(nextSavedCard);
            clearSavedPaymentError();
            setPaymentType(nextPaymentType);
            return;
        }
        if (nextPaymentType !== "credit") {
            setSelectedSavedCard(null);
        }
        clearSavedPaymentError();
        setPaymentType(nextPaymentType);
    };
    var handleTabChange = function (nextTab) {
        setSelectedPaymentTab(nextTab);
        if (nextTab === "pay-later") {
            if (cashOption && canSelectPayment && paymentType !== "cash") {
                setSelectedSavedCard(null);
                clearSavedPaymentError();
                setPaymentType("cash");
            }
            return;
        }
        if (paymentType === "cash") {
            clearSavedPaymentError();
            setPaymentType("none");
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "vertical-checkout-payment-options", "aria-live": "polite" },
            !canSelectPayment ? (React.createElement("p", { className: "vertical-checkout-error", role: "status" }, PAYMENT_SELECTION_REQUIREMENTS_MESSAGE)) : null,
            hasBothPaymentTabs ? (React.createElement(RadixTabs, { value: selectedPaymentTab, onValueChange: handleTabChange },
                React.createElement(RadixTabsList, { className: "vertical-checkout-payment-tabs-list", ariaLabel: "Choose when to pay" },
                    React.createElement(RadixTabsTrigger, { value: "pay-now", disabled: !canShowPayNowTab }, "Pay Online"),
                    canShowPayLaterTab ? (React.createElement(RadixTabsTrigger, { value: "pay-later" }, payLaterTabLabel)) : null),
                React.createElement(RadixTabsContent, { value: "pay-now", className: "vertical-checkout-payment-tab-content" }, canShowPayNowTab ? (React.createElement(React.Fragment, null, optionsWithSavedCards.length > 0 ? (React.createElement(RadixRadioGroup, { name: "vertical-checkout-payment-type", options: optionsWithSavedCards, value: selectedPaymentValue, onValueChange: handlePaymentChange, orientation: "vertical", ariaLabel: "Choose pay now method", optionClassName: "vertical-checkout-payment-option" })) : null)) : null),
                React.createElement(RadixTabsContent, { value: "pay-later", className: "vertical-checkout-payment-tab-content" }, canShowPayLaterTab ? (React.createElement(RadixRadioGroup, { name: "vertical-checkout-payment-type-cash", options: cashOnlyOptions, value: selectedPaymentValue, onValueChange: handlePaymentChange, orientation: "vertical", ariaLabel: payLaterTabLabel, optionClassName: "vertical-checkout-payment-option" })) : null))) : null,
            !hasBothPaymentTabs && canShowPayNowTab ? (React.createElement(React.Fragment, null, optionsWithSavedCards.length > 0 ? (React.createElement(RadixRadioGroup, { name: "vertical-checkout-payment-type", options: optionsWithSavedCards, value: selectedPaymentValue, onValueChange: handlePaymentChange, orientation: "vertical", ariaLabel: "Choose pay now method", optionClassName: "vertical-checkout-payment-option" })) : null)) : null,
            !hasBothPaymentTabs && !canShowPayNowTab && canShowPayLaterTab ? (React.createElement(RadixRadioGroup, { name: "vertical-checkout-payment-type-cash", options: cashOnlyOptions, value: selectedPaymentValue, onValueChange: handlePaymentChange, orientation: "vertical", ariaLabel: payLaterTabLabel, optionClassName: "vertical-checkout-payment-option" })) : null),
        React.createElement(PaymentErrorNotice, { error: savedPaymentError, onDismiss: clearSavedPaymentError }),
        paymentType === "cash" ? (React.createElement(React.Fragment, null,
            React.createElement("div", { className: "mt-2" },
                React.createElement(OrderSummary, { ncaFeeDescription: getXplorPayDebitFeeDescriptor(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.processor, sessionStore.nca_title) })),
            React.createElement(RadixButton, { type: "button", className: "radix-submit-button place-order-cash-button", onClick: onFinalizeCashOrder, disabled: !canSelectPayment || isSubmittingCashOrder }, isSubmittingCashOrder ? "Placing order..." : "Place order"))) : null,
        creditPaymentsEnabled && paymentType.startsWith("saved-") && selectedSavedCard ? (React.createElement(React.Fragment, null,
            (processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.processor) === "xplorpay" ? (React.createElement(AVSCollectionDetails, { onSubmit: function () { }, alwaysVisible: true, showAddress: false })) : null,
            React.createElement("div", { className: "mt-2" },
                React.createElement(OrderSummary, { ncaFeeDescription: getXplorPayDebitFeeDescriptor(processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.processor, sessionStore.nca_title) })),
            React.createElement(RadixButton, { type: "button", className: "radix-submit-button place-order-cash-button", onClick: function () {
                    void payWithSavedCard();
                }, disabled: isSavedPaymentSubmitting || ((processorInfo === null || processorInfo === void 0 ? void 0 : processorInfo.processor) === "xplorpay" && !isAvsValid) }, isSavedPaymentSubmitting ? "Processing payment..." : "Pay with ".concat(selectedSavedCard.friendly_name)))) : null));
};
var getErrorStatus = function (error) {
    var _a;
    if (!error || typeof error !== "object") {
        return null;
    }
    var status = (_a = error.response) === null || _a === void 0 ? void 0 : _a.status;
    return typeof status === "number" ? status : null;
};
var getApiErrorMessage = function (error, fallbackMessage) {
    var _a, _b;
    if (!error || typeof error !== "object") {
        return fallbackMessage;
    }
    var response = error.response;
    var data = response === null || response === void 0 ? void 0 : response.data;
    var friendlyName = String(((_a = data === null || data === void 0 ? void 0 : data.friendly) === null || _a === void 0 ? void 0 : _a.name) || (data === null || data === void 0 ? void 0 : data.name) || "").trim();
    var friendlyDescription = String(((_b = data === null || data === void 0 ? void 0 : data.friendly) === null || _b === void 0 ? void 0 : _b.description) || (data === null || data === void 0 ? void 0 : data.description) || "").trim();
    if (friendlyName && friendlyDescription) {
        return "".concat(friendlyName, " ").concat(friendlyDescription).trim();
    }
    if (friendlyDescription) {
        return friendlyDescription;
    }
    if (friendlyName) {
        return friendlyName;
    }
    var message = String((data === null || data === void 0 ? void 0 : data.message) || "").trim();
    return message || fallbackMessage;
};
export var VerticalCheckout = function () {
    var _a;
    var _b = useState(function () { return getPendingDealCode(); }), pendingDealCode = _b[0], setPendingDealCode = _b[1];
    var store = getSessionStore();
    var _c = useServiceTypes(), serviceTypes = _c.serviceTypes, serviceTypesAreLoading = _c.isLoading;
    var _d = useAuth(), history = _d.history, user = _d.user, createUser = _d.createUser, createAddress = _d.createAddress, updateUserProfile = _d.updateUserProfile, checkForUserData = _d.checkForUserData, refreshUserHistory = _d.refreshUserHistory;
    var _e = useUI(), orderAddressDetailsFlyoutIsOpen = _e.orderAddressDetailsFlyoutIsOpen, setOrderAddressDetailsFlyoutIsOpen = _e.setOrderAddressDetailsFlyoutIsOpen, openSignInModal = _e.openSignInModal, openSuggestiveSell = _e.openSuggestiveSell, showToast = _e.showToast, storeUsesRewards = _e.storeUsesRewards;
    var _f = useOrderIntent(), serviceType = _f.serviceType, serviceTypeId = _f.serviceTypeId, setCartServiceType = _f.setCartServiceType, setCurrentAddress = _f.setCurrentAddress, currentAddress = _f.currentAddress, orderTiming = _f.orderTiming, futureOrderDatetime = _f.futureOrderDatetime, setAsapOrder = _f.setAsapOrder, setFutureOrder = _f.setFutureOrder, futureDates = _f.futureDates, selectedFutureDate = _f.selectedFutureDate, futureDateHours = _f.futureDateHours, futureAvailabilityMessage = _f.futureAvailabilityMessage, futureAvailabilityLoading = _f.futureAvailabilityLoading, setSelectedFutureDate = _f.setSelectedFutureDate, validateFutureOrderDatetime = _f.validateFutureOrderDatetime, finalizeCashOrder = _f.finalizeCashOrder, serviceTypeEligibility = _f.serviceTypeEligibility;
    var _g = useCart(), items = _g.items, cart = _g.cart, cartIsLoading = _g.isLoading, updateTip = _g.updateTip, getCart = _g.getCart, updateItem = _g.updateItem, editingItem = _g.editingItem;
    var _h = useCheckout(), isCurbsideOrder = _h.isCurbsideOrder, setIsCurbsideOrder = _h.setIsCurbsideOrder, shouldSendSmsAlerts = _h.shouldSendSmsAlerts, setShouldSendSmsAlerts = _h.setShouldSendSmsAlerts, hasMarketingOptIn = _h.hasMarketingOptIn, setHasMarketingOptIn = _h.setHasMarketingOptIn, setSmsNumber = _h.setSmsNumber, selectedTip = _h.selectedTip, setSelectedTip = _h.setSelectedTip, paymentType = _h.paymentType, setPaymentType = _h.setPaymentType, applyDiscount = _h.applyDiscount, clearDiscounts = _h.clearDiscounts;
    var _j = useState(false), isOrderDetailsExpanded = _j[0], setIsOrderDetailsExpanded = _j[1];
    var _k = useState(false), isTipUpdating = _k[0], setIsTipUpdating = _k[1];
    var _l = useState(null), tipSelectionOverride = _l[0], setTipSelectionOverride = _l[1];
    var _m = useState(false), minimumDeliveryModalOpen = _m[0], setMinimumDeliveryModalOpen = _m[1];
    var _o = useState(null), cashOrderErrorMessage = _o[0], setCashOrderErrorMessage = _o[1];
    var _p = useState(false), isCreatingGuestUser = _p[0], setIsCreatingGuestUser = _p[1];
    var _q = useState(false), isSubmittingCashOrder = _q[0], setIsSubmittingCashOrder = _q[1];
    var _r = useState(false), isRedirectingAfterOrderSubmission = _r[0], setIsRedirectingAfterOrderSubmission = _r[1];
    var _s = useState(false), isPersistingDeferredAddress = _s[0], setIsPersistingDeferredAddress = _s[1];
    var _t = useState(false), isAccountManagementOpen = _t[0], setIsAccountManagementOpen = _t[1];
    var _u = useState(false), isSubmittingAccountDetails = _u[0], setIsSubmittingAccountDetails = _u[1];
    var _v = useState(function () { return shouldAutoCheckRewards(); }), wantsRewardsSignup = _v[0], setWantsRewardsSignup = _v[1];
    var _w = useState(false), isGuestPasswordVisible = _w[0], setIsGuestPasswordVisible = _w[1];
    var paymentSectionRef = useRef(null);
    var paymentHeadingRef = useRef(null);
    var orderTimeSectionRef = useRef(null);
    var hasTrackedViewCheckoutRef = useRef(false);
    var attemptedPendingDealCodesRef = useRef(new Set());
    var hasRequestedCheckoutUpsellRef = useRef(false);
    useEffect(function () {
        if (hasRequestedCheckoutUpsellRef.current || items.length === 0) {
            return;
        }
        hasRequestedCheckoutUpsellRef.current = true;
        openSuggestiveSell(undefined, "checkout");
    }, [items.length, openSuggestiveSell]);
    var handleCreditOrderComplete = function (order) {
        var _a;
        setIsRedirectingAfterOrderSubmission(true);
        var orderRecord = order;
        var orderLines = Array.isArray(orderRecord === null || orderRecord === void 0 ? void 0 : orderRecord.lines)
            ? orderRecord.lines
            : Array.isArray(orderRecord === null || orderRecord === void 0 ? void 0 : orderRecord.order_lines)
                ? orderRecord.order_lines
                : cartLines;
        try {
            AnalyticsManager.setPurchase(orderLines, orderRecord === null || orderRecord === void 0 ? void 0 : orderRecord.reference, (_a = orderRecord === null || orderRecord === void 0 ? void 0 : orderRecord.subtotal) !== null && _a !== void 0 ? _a : orderRecord === null || orderRecord === void 0 ? void 0 : orderRecord.total, orderRecord === null || orderRecord === void 0 ? void 0 : orderRecord.tax, orderRecord === null || orderRecord === void 0 ? void 0 : orderRecord.delivery_fee, orderRecord === null || orderRecord === void 0 ? void 0 : orderRecord.coupon_code, orderRecord === null || orderRecord === void 0 ? void 0 : orderRecord.user_id);
            GoogleAnalytics.orderComplete(orderRecord);
        }
        catch (error) {
            // Analytics must never block checkout completion or redirect.
            console.warn('[VerticalCheckout] Unable to track purchase analytics.', error);
        }
        var reference = String((orderRecord === null || orderRecord === void 0 ? void 0 : orderRecord.reference) || "").trim();
        if (reference.length > 0) {
            window.setTimeout(function () {
                window.location.assign("".concat(window.relativeURI(), "/my-order/").concat(reference));
            }, 650);
            return;
        }
        window.setTimeout(function () {
            window.location.assign("".concat(window.relativeURI(), "/order/status"));
        }, 650);
    };
    var _x = useForm({
        resolver: zodResolver(verticalCheckoutSchema),
        mode: "onBlur",
        defaultValues: {
            manualTipInput: "",
            orderComment: "",
            smsNumber: String((user === null || user === void 0 ? void 0 : user.phone) || ""),
            guestFirstName: String((user === null || user === void 0 ? void 0 : user.first_name) || ""),
            guestLastName: String((user === null || user === void 0 ? void 0 : user.last_name) || ""),
            guestEmail: String((user === null || user === void 0 ? void 0 : user.email) || ""),
            guestPhone: String((user === null || user === void 0 ? void 0 : user.phone) || ""),
            guestPassword: "",
            guestConfirmPassword: "",
        },
    }), control = _x.control, setValue = _x.setValue, trigger = _x.trigger, watch = _x.watch, errors = _x.formState.errors;
    var orderComment = watch("orderComment");
    var smsNumber = watch("smsNumber");
    var guestFirstName = watch("guestFirstName");
    var guestLastName = watch("guestLastName");
    var guestEmail = watch("guestEmail");
    var guestPhone = watch("guestPhone");
    var guestPassword = watch("guestPassword");
    var guestConfirmPassword = watch("guestConfirmPassword");
    var hasGuestPassword = String(guestPassword || "").trim().length > 0;
    var guestPasswordValue = String(guestPassword || "");
    var guestPasswordRequirements = [
        { label: "At least 8 characters", isMet: guestPasswordValue.length >= 8 },
        { label: "At least 1 uppercase letter", isMet: ValidationMethods.containsUppercaseLetter(guestPasswordValue) },
        { label: "At least 1 number", isMet: ValidationMethods.containsDigit(guestPasswordValue) },
        { label: "At least 1 special character", isMet: ValidationMethods.containsSpecialCharacter(guestPasswordValue) },
        { label: "Passwords match", isMet: Boolean(guestPasswordValue) && guestPasswordValue === String(guestConfirmPassword || "") },
    ];
    var debouncedTrigger = useDebouncedValidation(trigger);
    var isSmsNumberInputEnabled = shouldSendSmsAlerts || isCurbsideOrder;
    var prefillSmsNumberFromUserPhone = useCallback(function () {
        var userPhone = String((user === null || user === void 0 ? void 0 : user.phone) || "").trim();
        if (!userPhone || !ValidationMethods.isValidPhoneNumber(userPhone)) {
            return;
        }
        setValue("smsNumber", userPhone, { shouldValidate: true, shouldDirty: true });
    }, [setValue, user === null || user === void 0 ? void 0 : user.phone]);
    var handleCurbsideOrderChange = useCallback(function (checked) {
        var nextIsCurbsideOrder = Boolean(checked);
        setIsCurbsideOrder(nextIsCurbsideOrder);
        if (nextIsCurbsideOrder) {
            prefillSmsNumberFromUserPhone();
        }
    }, [prefillSmsNumberFromUserPhone, setIsCurbsideOrder]);
    var handleSmsAlertsChange = useCallback(function (checked) {
        var nextShouldSendSmsAlerts = Boolean(checked);
        setShouldSendSmsAlerts(nextShouldSendSmsAlerts);
        if (nextShouldSendSmsAlerts) {
            prefillSmsNumberFromUserPhone();
        }
    }, [prefillSmsNumberFromUserPhone, setShouldSendSmsAlerts]);
    useEffect(function () {
        document.body.classList.add(VERTICAL_CHECKOUT_BODY_CLASS);
        return function () {
            document.body.classList.remove(VERTICAL_CHECKOUT_BODY_CLASS);
        };
    }, []);
    useEffect(function () {
        setSmsNumber(String(smsNumber || ""));
    }, [setSmsNumber, smsNumber]);
    var cartSubtotal = toNumber(cart === null || cart === void 0 ? void 0 : cart.subtotal);
    var isCateringOrder = toBoolean(store === null || store === void 0 ? void 0 : store.is_catering);
    var standardMinimumDeliveryAmount = parseMinimumDeliveryAmount(store === null || store === void 0 ? void 0 : store.minimum_delivery_amount);
    var cateringMinimumDeliveryAmount = isCateringOrder ? toNumber(store === null || store === void 0 ? void 0 : store.catering_minimum_order_delivery) : 0;
    var cateringMinimumPickupAmount = isCateringOrder ? toNumber(store === null || store === void 0 ? void 0 : store.catering_minimum_order_pickup) : 0;
    var minimumDeliveryAmount = cateringMinimumDeliveryAmount > 0 ? cateringMinimumDeliveryAmount : standardMinimumDeliveryAmount;
    var minimumDeliveryMet = minimumDeliveryAmount <= 0 || cartSubtotal >= minimumDeliveryAmount;
    var minimumPickupMet = cateringMinimumPickupAmount <= 0 || cartSubtotal >= cateringMinimumPickupAmount;
    var serviceTypeOptions = useMemo(function () {
        return serviceTypes.map(function (type) {
            var name = String(type.name || "Service");
            var typeIsDelivery = isDeliveryServiceType(type);
            var disabled = typeIsDelivery ? !minimumDeliveryMet : !minimumPickupMet;
            return toServiceTypeOption(type.id, name, disabled);
        });
    }, [minimumDeliveryMet, minimumPickupMet, serviceTypes]);
    var cartServiceTypeId = typeof (cart === null || cart === void 0 ? void 0 : cart.service_type_id) === "number" ? cart.service_type_id : null;
    var resolvedServiceTypeId = serviceTypeId !== null && serviceTypeId !== void 0 ? serviceTypeId : cartServiceTypeId;
    var selectedServiceTypeValue = resolvedServiceTypeId === null ? null : String(resolvedServiceTypeId);
    var activeServiceType = useMemo(function () {
        if (resolvedServiceTypeId !== null) {
            var matchingServiceType = serviceTypes.find(function (candidate) { return candidate.id === resolvedServiceTypeId; }) || null;
            if (matchingServiceType) {
                return matchingServiceType;
            }
        }
        return serviceType;
    }, [resolvedServiceTypeId, serviceType, serviceTypes]);
    var selectedServiceTypeIsUnavailableForMenu = resolvedServiceTypeId !== null
        && !serviceTypes.some(function (candidate) { return candidate.id === resolvedServiceTypeId; });
    var serviceTypeUnavailableForMenuMessage = "The selected service type is not available for this menu. Please choose an available service type before placing your order.";
    var inferredDeliveryAddressRecord = useMemo(function () {
        var temporaryAddress = cart === null || cart === void 0 ? void 0 : cart.address_tmp;
        if (!temporaryAddress || typeof temporaryAddress !== "string") {
            return null;
        }
        var parsedAddress = JSON.parse(temporaryAddress);
        return {
            address: String(parsedAddress.address || "").trim(),
            address2: String(parsedAddress.address2 || "").trim(),
            city: String(parsedAddress.city || "").trim(),
            state: String(parsedAddress.state || "").trim(),
            zip: String(parsedAddress.zip || "").trim()
        };
    }, [cart]);
    var inferredDeliveryAddress = useMemo(function () {
        return formatDeliveryAddress(inferredDeliveryAddressRecord);
    }, [inferredDeliveryAddressRecord]);
    var selectedDeliveryAddress = useMemo(function () {
        var formattedCurrentAddress = formatDeliveryAddress(currentAddress);
        return formattedCurrentAddress || inferredDeliveryAddress;
    }, [currentAddress, inferredDeliveryAddress]);
    var selectedDeliveryAddressLines = useMemo(function () {
        return getFormattedDeliveryAddressLines((currentAddress || inferredDeliveryAddressRecord));
    }, [currentAddress, inferredDeliveryAddressRecord]);
    var isDeliverySelected = isDeliveryServiceType(activeServiceType);
    var deliveryServiceType = useMemo(function () {
        return serviceTypes.find(function (candidate) { return isDeliveryServiceType(candidate); }) || null;
    }, [serviceTypes]);
    var pickupServiceType = useMemo(function () {
        var explicitPickupServiceType = serviceTypes.find(function (candidate) { return Number(candidate.pos_id) === 3; }) || null;
        if (explicitPickupServiceType) {
            return explicitPickupServiceType;
        }
        return serviceTypes.find(function (candidate) { return !isDeliveryServiceType(candidate); }) || null;
    }, [serviceTypes]);
    var tipPercentages = useMemo(function () {
        return getTipPercentages(store === null || store === void 0 ? void 0 : store.tip_options);
    }, [store === null || store === void 0 ? void 0 : store.tip_options]);
    var selectedTipPercentage = useMemo(function () {
        if (selectedTip === null || cartSubtotal <= 0) {
            return null;
        }
        var selectedTipPercentageValue = (selectedTip / cartSubtotal) * 100;
        var roundedSelectedPercentage = Math.round(selectedTipPercentageValue * 100) / 100;
        for (var _i = 0, tipPercentages_1 = tipPercentages; _i < tipPercentages_1.length; _i++) {
            var percentage = tipPercentages_1[_i];
            var expectedTip = cartSubtotal * (percentage / 100);
            if (Math.abs(expectedTip - selectedTip) < 0.01) {
                return String(percentage);
            }
            if (Math.abs(roundedSelectedPercentage - percentage) < 0.01) {
                return String(percentage);
            }
        }
        return null;
    }, [selectedTip, cartSubtotal, tipPercentages]);
    var selectedTipOption = useMemo(function () {
        if (tipSelectionOverride === "custom") {
            return "custom";
        }
        if (selectedTipPercentage !== null) {
            return selectedTipPercentage;
        }
        if (selectedTip !== null) {
            return "custom";
        }
        return null;
    }, [selectedTip, selectedTipPercentage, tipSelectionOverride]);
    var cartLines = useMemo(function () {
        if (items.length > 0) {
            return items;
        }
        if (cart && typeof cart === "object") {
            var asRecord = cart;
            if (Array.isArray(asRecord.lines)) {
                return asRecord.lines;
            }
            if (Array.isArray(asRecord.cart_lines)) {
                return asRecord.cart_lines;
            }
            if (Array.isArray(asRecord.items)) {
                return asRecord.items;
            }
        }
        return [];
    }, [items, cart]);
    var itemCount = useMemo(function () {
        return cartLines.reduce(function (count, line) {
            var quantity = typeof line.quantity === "number" ? line.quantity : 0;
            return count + quantity;
        }, 0);
    }, [cartLines]);
    useEffect(function () {
        if (hasTrackedViewCheckoutRef.current || cart === null) {
            return;
        }
        AnalyticsManager.reviewCart(cartLines, (activeServiceType === null || activeServiceType === void 0 ? void 0 : activeServiceType.name) || "", function () {
            GoogleAnalytics.viewCheckout();
        });
        hasTrackedViewCheckoutRef.current = true;
    }, [activeServiceType === null || activeServiceType === void 0 ? void 0 : activeServiceType.name, cart, cartLines]);
    var selectedServiceTypeIsDelivery = isDeliveryServiceType(activeServiceType);
    var selectedServiceTypeMinimumAmount = selectedServiceTypeIsDelivery ? minimumDeliveryAmount : cateringMinimumPickupAmount;
    var selectedServiceTypeMinimumMet = selectedServiceTypeMinimumAmount <= 0 || cartSubtotal >= selectedServiceTypeMinimumAmount;
    var cateringRequiresPrePayment = isCateringOrder && (selectedServiceTypeIsDelivery
        ? toBoolean(store === null || store === void 0 ? void 0 : store.catering_requires_payment_delivery)
        : toBoolean(store === null || store === void 0 ? void 0 : store.catering_requires_payment_pickup));
    var creditPaymentsEnabled = Number((activeServiceType === null || activeServiceType === void 0 ? void 0 : activeServiceType.allow_credit) || 0) === 1;
    var cateringPaymentMustCallStore = cateringRequiresPrePayment && !creditPaymentsEnabled;
    var paymentOptions = useMemo(function () {
        var options = [];
        var allowsCash = Number((activeServiceType === null || activeServiceType === void 0 ? void 0 : activeServiceType.allow_cash) || 0) === 1 && !cateringRequiresPrePayment;
        var isPickupWithPayInStoreOnly = allowsCash
            && !creditPaymentsEnabled
            && !isDeliveryServiceType(activeServiceType);
        if (allowsCash) {
            options.push({
                value: "cash",
                label: (React.createElement("span", { className: "vertical-checkout-payment-label-wrap" },
                    React.createElement("span", { className: "vertical-checkout-payment-icon", "aria-hidden": "true" },
                        React.createElement(CashGlyph, null)),
                    React.createElement("span", null, isPickupWithPayInStoreOnly ? "Cash or Credit in Store" : "Cash"))),
                description: (activeServiceType === null || activeServiceType === void 0 ? void 0 : activeServiceType.pos_id) === 1 ? "Pay on Delivery." : "Pay at Pickup.",
                ariaLabel: isPickupWithPayInStoreOnly ? "Pay with cash or credit in store" : "Pay with cash",
            });
        }
        if (creditPaymentsEnabled) {
            options.push({
                value: "credit",
                label: (React.createElement("span", { className: "vertical-checkout-payment-label-wrap" },
                    React.createElement("span", { className: "vertical-checkout-payment-icon", "aria-hidden": "true" },
                        React.createElement(CreditCardGlyph, null)),
                    React.createElement("span", null, "Pay with Card"))),
                description: "Use credit or debit card.",
                ariaLabel: "Pay with card",
            });
        }
        return options;
    }, [activeServiceType, cateringRequiresPrePayment, creditPaymentsEnabled]);
    var showCardTypeBadges = creditPaymentsEnabled && paymentType !== "cash";
    useEffect(function () {
        if (paymentType === "cash" && cateringRequiresPrePayment) {
            setPaymentType("none");
        }
    }, [cateringRequiresPrePayment, paymentType, setPaymentType]);
    var hasFutureOrdering = serviceTypeEligibility.canFuture && getFutureOrderDays() >= 1;
    var showOrderComments = Boolean(Number((store === null || store === void 0 ? void 0 : store.show_order_comments) || 0));
    var hasCheckoutUser = Boolean(user === null || user === void 0 ? void 0 : user.id);
    var shouldCollectGuestDetails = !hasCheckoutUser;
    var isRewardsMember = useMemo(function () {
        if (!user) {
            return false;
        }
        var rawRewardsValue = user.uses_rewards;
        if (typeof rawRewardsValue === "boolean") {
            return rawRewardsValue;
        }
        if (typeof rawRewardsValue === "number") {
            return rawRewardsValue === 1;
        }
        if (typeof rawRewardsValue === "string") {
            var normalizedRewardsValue = rawRewardsValue.trim().toLowerCase();
            return normalizedRewardsValue === "1" || normalizedRewardsValue === "true";
        }
        return false;
    }, [user]);
    var currentRewardsPoints = (_a = history === null || history === void 0 ? void 0 : history.current_points) !== null && _a !== void 0 ? _a : "--";
    var rewardsSignupLabel = "Check this box to start earning reward points";
    var shouldSignUpRewards = storeUsesRewards && !isRewardsMember && wantsRewardsSignup;
    var hasPersistedMarketingOptIn = useMemo(function () {
        if (!user) {
            return false;
        }
        var rawMarketingOptInValue = user.has_marketing_opt_in;
        if (typeof rawMarketingOptInValue === "boolean") {
            return rawMarketingOptInValue;
        }
        if (typeof rawMarketingOptInValue === "number") {
            return rawMarketingOptInValue === 1;
        }
        if (typeof rawMarketingOptInValue === "string") {
            var normalizedMarketingOptIn = rawMarketingOptInValue.trim().toLowerCase();
            return normalizedMarketingOptIn === "1" || normalizedMarketingOptIn === "true";
        }
        return false;
    }, [user]);
    var guestUserValue = user === null || user === void 0 ? void 0 : user.is_guest;
    var isGuestCheckoutUser = guestUserValue === true
        || guestUserValue === 1
        || (typeof guestUserValue === "string" && ["1", "true"].includes(guestUserValue.trim().toLowerCase()));
    var showMarketingOptInInOrderPreferences = hasCheckoutUser && !isGuestCheckoutUser && !hasPersistedMarketingOptIn;
    var showOrderPreferences = Boolean(Number(store === null || store === void 0 ? void 0 : store.has_inotify)) || showMarketingOptInInOrderPreferences;
    var appliedDiscountCodes = useMemo(function () {
        return getAppliedDiscountCodes(cart);
    }, [cart]);
    useEffect(function () {
        if (!shouldCollectGuestDetails) {
            return;
        }
        setValue("guestFirstName", String((user === null || user === void 0 ? void 0 : user.first_name) || ""), { shouldValidate: false });
        setValue("guestLastName", String((user === null || user === void 0 ? void 0 : user.last_name) || ""), { shouldValidate: false });
        setValue("guestEmail", String((user === null || user === void 0 ? void 0 : user.email) || ""), { shouldValidate: false });
        setValue("guestPhone", String((user === null || user === void 0 ? void 0 : user.phone) || ""), { shouldValidate: false });
        setValue("smsNumber", String((user === null || user === void 0 ? void 0 : user.phone) || ""), { shouldValidate: false });
        setValue("guestPassword", "", { shouldValidate: false });
    }, [setValue, shouldCollectGuestDetails, user === null || user === void 0 ? void 0 : user.email, user === null || user === void 0 ? void 0 : user.first_name, user === null || user === void 0 ? void 0 : user.last_name, user === null || user === void 0 ? void 0 : user.phone]);
    var setCartServiceTypeAndRefresh = useCallback(function (nextServiceType, address) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, setCartServiceType(nextServiceType, address)];
                case 1:
                    _a.sent();
                    return [4 /*yield*/, getCart()];
                case 2:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [getCart, setCartServiceType]);
    var setAsapOrderAndRefresh = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, setAsapOrder()];
                case 1:
                    _a.sent();
                    return [4 /*yield*/, getCart()];
                case 2:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [getCart, setAsapOrder]);
    var setFutureOrderAndRefresh = useCallback(function (datetime) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setFutureOrder(datetime);
                    return [4 /*yield*/, getCart()];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [getCart, setFutureOrder]);
    var setSelectedFutureDateAndRefresh = useCallback(function (date, shouldClearExistingDeferred) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, setSelectedFutureDate(date, shouldClearExistingDeferred)];
                case 1:
                    _a.sent();
                    return [4 /*yield*/, getCart()];
                case 2:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [getCart, setSelectedFutureDate]);
    var showSessionRecoveryToast = useCallback(function () {
        showToast("Your session expired and could not be recovered. Please refresh the page to continue ordering.", "warning", true, {
            label: "Refresh",
            onClick: function () { return window.location.reload(); },
        });
    }, [showToast]);
    var showServiceTypeUpdateErrorToast = useCallback(function (error, fallbackMessage) {
        if (getErrorStatus(error) === 419) {
            showSessionRecoveryToast();
            return;
        }
        showToast(getApiErrorMessage(error, fallbackMessage), "error", true, {
            label: "Refresh",
            onClick: function () { document.location.reload(); }
        });
    }, [showSessionRecoveryToast, showToast]);
    var handleServiceTypeChange = function (nextValue) {
        var nextId = Number(nextValue);
        if (!Number.isFinite(nextId)) {
            return;
        }
        var nextServiceType = serviceTypes.find(function (candidate) { return candidate.id === nextId; }) || null;
        if (!nextServiceType) {
            return;
        }
        var nextIsDelivery = isDeliveryServiceType(nextServiceType);
        var nextMinimumAmount = nextIsDelivery ? minimumDeliveryAmount : cateringMinimumPickupAmount;
        var nextMinimumMet = nextMinimumAmount <= 0 || cartSubtotal >= nextMinimumAmount;
        if (!nextMinimumMet) {
            setMinimumDeliveryModalOpen(true);
            return;
        }
        if (!nextIsDelivery) {
            setCartServiceTypeAndRefresh(nextServiceType, null).catch(function (error) {
                showServiceTypeUpdateErrorToast(error, "We couldn't update your service type. Please refresh the page try again.");
            });
            return;
        }
        if (!selectedDeliveryAddress) {
            setOrderAddressDetailsFlyoutIsOpen(true);
            return;
        }
        var deliveryAddressPayload = currentAddress || inferredDeliveryAddressRecord;
        if (!hasAddressFields(deliveryAddressPayload)) {
            showToast("Please add a complete delivery address before selecting delivery.", "error");
            setOrderAddressDetailsFlyoutIsOpen(true);
            return;
        }
        if (hasCheckoutUser) {
            ensureAddressCreatedWhenPossible(deliveryAddressPayload)
                .then(function (resolvedAddress) {
                setCurrentAddress(resolvedAddress);
                return setCartServiceTypeAndRefresh(nextServiceType, resolvedAddress);
            })
                .catch(function (error) {
                showServiceTypeUpdateErrorToast(error, "We couldn't update your service type. Please refresh the page try again.");
            });
            return;
        }
        setCartServiceTypeAndRefresh(nextServiceType, deliveryAddressPayload).catch(function (error) {
            showServiceTypeUpdateErrorToast(error, "We couldn't update your service type. Please refresh the page try again.");
        });
    };
    var handleCreateGuestUser = function () {
        if (isCreatingGuestUser) {
            return;
        }
        setIsCreatingGuestUser(true);
        void trigger(["guestFirstName", "guestLastName", "guestEmail", "guestPhone", "guestPassword"])
            .then(function (isValid) { return __awaiter(void 0, void 0, void 0, function () {
            var createUserResult, createdUser, deliveryAddressPayload, resolvedAddress;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!isValid) {
                            showToast("Please complete your information to continue.", "error");
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, createUser({
                                firstName: guestFirstName,
                                lastName: guestLastName,
                                email: guestEmail,
                                phone: guestPhone,
                                password: hasGuestPassword ? String(guestPassword) : "",
                                isGuest: !hasGuestPassword,
                                useRewards: shouldSignUpRewards,
                                checkoutAddressId: (_a = currentAddress === null || currentAddress === void 0 ? void 0 : currentAddress.id) !== null && _a !== void 0 ? _a : null,
                            })];
                    case 1:
                        createUserResult = _b.sent();
                        createdUser = createUserResult.user;
                        if (!createdUser) {
                            showToast(createUserResult.errorMessage || "We couldn't save your information. Please try again.", "error", true);
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, checkForUserData()];
                    case 2:
                        _b.sent();
                        deliveryAddressPayload = createUserResult.checkoutAddress
                            || currentAddress
                            || inferredDeliveryAddressRecord;
                        if (!deliveryAddressPayload) {
                            return [2 /*return*/];
                        }
                        if (createUserResult.checkoutAddress) {
                            setCurrentAddress(createUserResult.checkoutAddress);
                        }
                        return [4 /*yield*/, ensureAddressCreatedWhenPossible(deliveryAddressPayload, createdUser.id)];
                    case 3:
                        resolvedAddress = _b.sent();
                        if (resolvedServiceTypeId !== null) {
                            setCartServiceTypeAndRefresh(resolvedServiceTypeId, resolvedAddress).catch(function (error) {
                                showServiceTypeUpdateErrorToast(error, "We couldn't update your delivery address. Please try again.");
                            });
                        }
                        return [2 /*return*/];
                }
            });
        }); })
            .finally(function () {
            setIsCreatingGuestUser(false);
        });
    };
    var handleOrderLineEdit = function (line) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (Boolean(editingItem)) {
                        return [2 /*return*/];
                    }
                    if (typeof line.id === "undefined" || line.id === null || typeof line.category_id === "undefined" || line.category_id === null) {
                        return [2 /*return*/];
                    }
                    window.editLineComment = getLineComment(line.comment);
                    return [4 /*yield*/, updateItem(Number(line.id), Number(line.category_id))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); };
    var persistTip = function (tipAmount) { return __awaiter(void 0, void 0, void 0, function () {
        var _error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setSelectedTip(tipAmount);
                    setIsTipUpdating(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, updateTip(tipAmount)];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 3:
                    _error_1 = _a.sent();
                    showToast("Unable to update tip right now. Please try again.", "error");
                    return [3 /*break*/, 5];
                case 4:
                    setIsTipUpdating(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var handlePaymentTypeChange = function (nextPaymentType) {
        if (nextPaymentType === "cash" && paymentType !== "cash" && (selectedTip !== null && selectedTip !== void 0 ? selectedTip : 0) > 0) {
            setTipSelectionOverride(null);
            setValue("manualTipInput", "", { shouldValidate: true });
            void persistTip(null);
            showToast("Thanks for tipping! Cash tips can be given in person, so we've removed the tip from your online order.", "info");
        }
        setPaymentType(nextPaymentType);
    };
    var setTipFromPercentage = function (percentage) {
        var tipAmount = cartSubtotal * (percentage / 100);
        setTipSelectionOverride(null);
        void persistTip(tipAmount);
        setValue("manualTipInput", "", { shouldValidate: true });
    };
    var handleTipOptionChange = function (value) {
        if (value === "custom") {
            setTipSelectionOverride("custom");
            setValue("manualTipInput", "0", { shouldValidate: true, shouldDirty: true });
            void persistTip(0);
            return;
        }
        var parsed = Number(value);
        if (Number.isFinite(parsed)) {
            setTipFromPercentage(parsed);
        }
    };
    var handleManualTipChange = function (nextValue) {
        setTipSelectionOverride("custom");
        setValue("manualTipInput", nextValue, { shouldValidate: true, shouldDirty: true });
        var validation = verticalCheckoutSchema.shape.manualTipInput.safeParse(nextValue);
        if (!validation.success) {
            return;
        }
        if (nextValue.trim().length === 0) {
            void persistTip(null);
            return;
        }
        var tipValue = Number(nextValue);
        if (Number.isFinite(tipValue)) {
            void persistTip(tipValue);
        }
    };
    var handleManualTipBlur = function (currentValue) {
        var normalizedValue = String(currentValue || "").trim();
        if (!normalizedValue) {
            return;
        }
        var validation = verticalCheckoutSchema.shape.manualTipInput.safeParse(normalizedValue);
        if (!validation.success) {
            return;
        }
        var tipValue = Number(normalizedValue);
        if (!Number.isFinite(tipValue)) {
            return;
        }
        var formattedValue = tipValue.toFixed(2);
        if (formattedValue.length <= 6 && formattedValue !== normalizedValue) {
            setValue("manualTipInput", formattedValue, { shouldValidate: true, shouldDirty: true });
        }
    };
    var restaurantName = String((store === null || store === void 0 ? void 0 : store.name) || "").trim();
    var restaurantStreet = String((store === null || store === void 0 ? void 0 : store.address) || "").trim();
    var restaurantAddressLine = getAddressLine(store);
    var handleAddressFlyOutOpenChange = function (open) {
        setOrderAddressDetailsFlyoutIsOpen(open);
    };
    var handleApplyDiscount = useCallback(function (code) { return __awaiter(void 0, void 0, void 0, function () {
        var normalizedCode, error_1, message;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    normalizedCode = String(code || "").trim();
                    if (!normalizedCode) {
                        return [2 /*return*/, {
                                success: false,
                                message: "Please enter a promo code.",
                            }];
                    }
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, applyDiscount(normalizedCode)];
                case 2:
                    _a.sent();
                    return [4 /*yield*/, getCart()];
                case 3:
                    _a.sent();
                    return [2 /*return*/, {
                            success: true,
                            message: "Promo code ".concat(normalizedCode, " applied successfully."),
                        }];
                case 4:
                    error_1 = _a.sent();
                    message = getApiErrorMessage(error_1, "We couldn't apply code ".concat(normalizedCode, ". Please try another code."));
                    return [2 /*return*/, {
                            success: false,
                            message: message,
                        }];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [applyDiscount, getCart]);
    var handleRemoveDiscount = useCallback(function (code) { return __awaiter(void 0, void 0, void 0, function () {
        var normalizedCode, error_2, message;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    normalizedCode = String(code || "").trim();
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, clearDiscounts()];
                case 2:
                    _a.sent();
                    return [4 /*yield*/, getCart()];
                case 3:
                    _a.sent();
                    return [2 /*return*/, {
                            success: true,
                            message: normalizedCode
                                ? "Promo code ".concat(normalizedCode, " removed.")
                                : "Promo code removed.",
                        }];
                case 4:
                    error_2 = _a.sent();
                    message = getApiErrorMessage(error_2, "We couldn't remove your promo code right now. Please try again.");
                    return [2 /*return*/, {
                            success: false,
                            message: message,
                        }];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [clearDiscounts, getCart]);
    useEffect(function () {
        var nextPendingDealCode = getPendingDealCode();
        if (nextPendingDealCode !== pendingDealCode) {
            setPendingDealCode(nextPendingDealCode);
        }
    }, [pendingDealCode]);
    useEffect(function () {
        var normalizedPendingCode = pendingDealCode.trim();
        if (!normalizedPendingCode) {
            return;
        }
        if (resolvedServiceTypeId === null) {
            return;
        }
        var normalizedKey = normalizedPendingCode.toLowerCase();
        if (attemptedPendingDealCodesRef.current.has(normalizedKey)) {
            return;
        }
        var isAlreadyApplied = appliedDiscountCodes.some(function (code) {
            return code.trim().toLowerCase() === normalizedKey;
        });
        if (isAlreadyApplied) {
            return;
        }
        attemptedPendingDealCodesRef.current.add(normalizedKey);
        void handleApplyDiscount(normalizedPendingCode).then(function (result) {
            if (!result.success) {
                showToast(result.message, "error", true);
            }
        });
    }, [appliedDiscountCodes, handleApplyDiscount, pendingDealCode, resolvedServiceTypeId, showToast]);
    var syncAddressInContext = useCallback(function (candidateAddress, createdAddress) {
        var nextAddress = __assign(__assign({}, candidateAddress), (createdAddress || {}));
        return nextAddress;
    }, []);
    var ensureAddressCreatedWhenPossible = useCallback(function (candidateAddress_1, userIdOverride_1) {
        var args_1 = [];
        for (var _i = 2; _i < arguments.length; _i++) {
            args_1[_i - 2] = arguments[_i];
        }
        return __awaiter(void 0, __spreadArray([candidateAddress_1, userIdOverride_1], args_1, true), void 0, function (candidateAddress, userIdOverride, notifyOnDuplicate, forceCreateIfNoExactMatch) {
            var existingAddressId, cartAddressId, persistedAddressId, historyAddresses, normalizedCandidate, matchingHistoryAddress, matchedAddressById, createdAddress;
            if (notifyOnDuplicate === void 0) { notifyOnDuplicate = false; }
            if (forceCreateIfNoExactMatch === void 0) { forceCreateIfNoExactMatch = false; }
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        existingAddressId = String((candidateAddress === null || candidateAddress === void 0 ? void 0 : candidateAddress.id) || "").trim();
                        cartAddressId = String((cart === null || cart === void 0 ? void 0 : cart.address_id) || "").trim();
                        persistedAddressId = existingAddressId || cartAddressId;
                        historyAddresses = history === null || history === void 0 ? void 0 : history.addresses;
                        normalizedCandidate = getAddressCreationCandidate(candidateAddress);
                        if (existingAddressId.length > 0) {
                            return [2 /*return*/, candidateAddress];
                        }
                        if (!normalizedCandidate) {
                            return [2 /*return*/, candidateAddress];
                        }
                        matchingHistoryAddress = getAddressHistoryMatch(historyAddresses, normalizedCandidate);
                        if (matchingHistoryAddress) {
                            if (notifyOnDuplicate) {
                                showToast("That address already exists. We selected it for you.", "success");
                            }
                            return [2 /*return*/, syncAddressInContext(candidateAddress, matchingHistoryAddress)];
                        }
                        if (!forceCreateIfNoExactMatch && persistedAddressId.length > 0) {
                            if (!Array.isArray(historyAddresses)) {
                                return [2 /*return*/, __assign(__assign({}, candidateAddress), { id: persistedAddressId })];
                            }
                            matchedAddressById = historyAddresses.find(function (address) {
                                return String((address === null || address === void 0 ? void 0 : address.id) || "").trim() === persistedAddressId;
                            });
                            if (matchedAddressById) {
                                return [2 /*return*/, syncAddressInContext(candidateAddress, matchedAddressById)];
                            }
                        }
                        return [4 /*yield*/, createAddress(__assign(__assign({}, normalizedCandidate), { userId: userIdOverride !== null && userIdOverride !== void 0 ? userIdOverride : user === null || user === void 0 ? void 0 : user.id }))];
                    case 1:
                        createdAddress = _a.sent();
                        if (!createdAddress) {
                            return [2 /*return*/, candidateAddress];
                        }
                        return [2 /*return*/, syncAddressInContext(candidateAddress, createdAddress)];
                }
            });
        });
    }, [cart, createAddress, history === null || history === void 0 ? void 0 : history.addresses, showToast, syncAddressInContext, user === null || user === void 0 ? void 0 : user.id]);
    var handleAddressApply = function (address) { return __awaiter(void 0, void 0, void 0, function () {
        var isExplicitNewAddressSelection, resolvedAddress, _a, error_3;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!address) {
                        return [2 /*return*/];
                    }
                    if (!hasAddressFields(address)) {
                        showToast("Please enter a complete delivery address.", "error");
                        return [2 /*return*/];
                    }
                    if (!deliveryServiceType) {
                        showToast("Delivery service type is unavailable right now.", "error");
                        return [2 /*return*/];
                    }
                    if (!minimumDeliveryMet) {
                        setMinimumDeliveryModalOpen(true);
                        return [2 /*return*/];
                    }
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 6, , 7]);
                    isExplicitNewAddressSelection = String(address.id || "").trim().length === 0;
                    if (!hasCheckoutUser) return [3 /*break*/, 3];
                    return [4 /*yield*/, ensureAddressCreatedWhenPossible(address, undefined, true, isExplicitNewAddressSelection)];
                case 2:
                    _a = _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    _a = address;
                    _b.label = 4;
                case 4:
                    resolvedAddress = _a;
                    setCurrentAddress(resolvedAddress);
                    return [4 /*yield*/, setCartServiceTypeAndRefresh(deliveryServiceType, resolvedAddress)];
                case 5:
                    _b.sent();
                    return [3 /*break*/, 7];
                case 6:
                    error_3 = _b.sent();
                    showServiceTypeUpdateErrorToast(error_3, "We couldn't update your delivery address. Please try again.");
                    throw error_3;
                case 7: return [2 /*return*/];
            }
        });
    }); };
    useEffect(function () {
        if (!hasCheckoutUser || !isDeliverySelected || isPersistingDeferredAddress) {
            return;
        }
        var addressCandidate = currentAddress || inferredDeliveryAddressRecord;
        if (!addressCandidate) {
            return;
        }
        var existingAddressId = String(addressCandidate.id || "").trim();
        if (existingAddressId.length > 0) {
            return;
        }
        setIsPersistingDeferredAddress(true);
        ensureAddressCreatedWhenPossible(addressCandidate)
            .then(function (resolvedAddress) {
            setCurrentAddress(resolvedAddress);
            if (resolvedServiceTypeId !== null) {
                return setCartServiceTypeAndRefresh(resolvedServiceTypeId, resolvedAddress);
            }
            return Promise.resolve();
        })
            .catch(function () {
            // no-op: address persistence can retry on subsequent state changes
        })
            .finally(function () {
            setIsPersistingDeferredAddress(false);
        });
    }, [
        currentAddress,
        ensureAddressCreatedWhenPossible,
        hasCheckoutUser,
        inferredDeliveryAddressRecord,
        isDeliverySelected,
        isPersistingDeferredAddress,
        resolvedServiceTypeId,
        setCartServiceTypeAndRefresh,
        setCurrentAddress,
    ]);
    var guestDetailsValidation = guestDetailsSchema.safeParse({
        guestFirstName: guestFirstName,
        guestLastName: guestLastName,
        guestEmail: guestEmail,
        guestPhone: guestPhone,
    });
    var isGuestContinueDisabled = isCreatingGuestUser || !guestDetailsValidation.success;
    var guestRequirementsMet = !shouldCollectGuestDetails || guestDetailsValidation.success;
    var hasServiceTypeSelected = resolvedServiceTypeId !== null;
    var deferredDatetimeFromCart = typeof (cart === null || cart === void 0 ? void 0 : cart.deferred_datetime) === "string" && cart.deferred_datetime.trim().length > 0
        ? cart.deferred_datetime
        : null;
    var hasDeferredDatetimeFromCart = Boolean(deferredDatetimeFromCart);
    var effectiveOrderTiming = !serviceTypeEligibility.canAsap && serviceTypeEligibility.canFuture
        ? "future"
        : (orderTiming === "future" || hasDeferredDatetimeFromCart) ? "future" : "asap";
    var effectiveFutureOrderDatetime = effectiveOrderTiming === "future"
        ? (deferredDatetimeFromCart || futureOrderDatetime)
        : null;
    var hasOrderTimingSelected = effectiveOrderTiming === "future"
        ? Boolean(effectiveFutureOrderDatetime)
        : serviceTypeEligibility.canAsap;
    var deliveryRequirementMet = !isDeliverySelected || Boolean(selectedDeliveryAddress);
    var canSelectPayment = hasServiceTypeSelected
        && hasOrderTimingSelected
        && deliveryRequirementMet
        && guestRequirementsMet
        && selectedServiceTypeMinimumMet
        && !selectedServiceTypeIsUnavailableForMenu
        && !cateringPaymentMustCallStore;
    var scrollToOrderTimeSelection = useCallback(function () {
        var scrollTarget = orderTimeSectionRef.current;
        var heading = document.getElementById("vertical-order-time-heading");
        if (!scrollTarget) {
            heading === null || heading === void 0 ? void 0 : heading.focus({ preventScroll: true });
            return;
        }
        var reducedMotionPreferred = typeof window !== "undefined"
            && typeof window.matchMedia === "function"
            && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        var header = document.querySelector(".radix-navbar--sticky");
        var headerHeight = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
        var orderIntentNavbar = document.querySelector(".order-intent-navbar");
        var orderIntentHeight = orderIntentNavbar instanceof HTMLElement && orderIntentNavbar.getAttribute("aria-hidden") !== "true"
            ? orderIntentNavbar.getBoundingClientRect().height
            : 0;
        var categorySelector = document.querySelector(".category-navigation");
        var categorySelectorHeight = categorySelector instanceof HTMLElement ? categorySelector.getBoundingClientRect().height : 0;
        var offset = headerHeight + orderIntentHeight + categorySelectorHeight + 12;
        var targetTop = window.scrollY + scrollTarget.getBoundingClientRect().top - offset;
        window.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: reducedMotionPreferred ? "auto" : "smooth",
        });
        heading === null || heading === void 0 ? void 0 : heading.focus({ preventScroll: true });
    }, []);
    useEffect(function () {
        if (paymentType !== "credit" || !canSelectPayment) {
            return;
        }
        var reducedMotionPreferred = typeof window !== "undefined"
            && typeof window.matchMedia === "function"
            && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        var scrollToCardDetails = function () {
            var _a;
            var cardDetailsHeading = document.querySelector("[data-card-details-heading='true']");
            var scrollTarget = cardDetailsHeading !== null && cardDetailsHeading !== void 0 ? cardDetailsHeading : paymentSectionRef.current;
            if (scrollTarget) {
                var header = document.querySelector(".radix-navbar--sticky");
                var headerHeight = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
                var orderIntentNavbar = document.querySelector(".order-intent-navbar");
                var orderIntentHeight = orderIntentNavbar instanceof HTMLElement && orderIntentNavbar.getAttribute("aria-hidden") !== "true"
                    ? orderIntentNavbar.getBoundingClientRect().height
                    : 0;
                var categorySelector = document.querySelector(".category-navigation");
                var categorySelectorHeight = categorySelector instanceof HTMLElement ? categorySelector.getBoundingClientRect().height : 0;
                var offset = headerHeight + orderIntentHeight + categorySelectorHeight + 12;
                var targetTop = window.scrollY + scrollTarget.getBoundingClientRect().top - offset;
                window.scrollTo({
                    top: Math.max(targetTop, 0),
                    behavior: reducedMotionPreferred ? "auto" : "smooth",
                });
            }
            if (cardDetailsHeading) {
                cardDetailsHeading.focus({ preventScroll: true });
                return;
            }
            (_a = paymentHeadingRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
        };
        var animationFrameId = window.requestAnimationFrame(scrollToCardDetails);
        return function () {
            window.cancelAnimationFrame(animationFrameId);
        };
    }, [paymentType, canSelectPayment]);
    var handleAddMoreFood = function () {
        setMinimumDeliveryModalOpen(false);
        window.location.assign("".concat(window.relativeURI(), "/menu"));
    };
    // Add isChangingServiceType state after nearby checkout/UI loading states
    var _y = useState(false), isChangingServiceType = _y[0], setIsChangingServiceType = _y[1];
    var handleDeliveryAddressChangeClick = function () {
        setOrderAddressDetailsFlyoutIsOpen(true);
    };
    var accountManagementDefaultValues = useMemo(function () {
        var _a, _b, _c, _d;
        return ({
            firstName: String((_a = user === null || user === void 0 ? void 0 : user.first_name) !== null && _a !== void 0 ? _a : ""),
            lastName: String((_b = user === null || user === void 0 ? void 0 : user.last_name) !== null && _b !== void 0 ? _b : ""),
            email: String((_c = user === null || user === void 0 ? void 0 : user.email) !== null && _c !== void 0 ? _c : ""),
            phone: String((_d = user === null || user === void 0 ? void 0 : user.phone) !== null && _d !== void 0 ? _d : ""),
        });
    }, [user]);
    var handleAccountDetailsUpdate = function (values) { return __awaiter(void 0, void 0, void 0, function () {
        var wasUpdated;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (isSubmittingAccountDetails) {
                        return [2 /*return*/];
                    }
                    setIsSubmittingAccountDetails(true);
                    return [4 /*yield*/, updateUserProfile({
                            firstName: values.firstName,
                            lastName: values.lastName,
                            email: values.email,
                            phone: values.phone,
                        })];
                case 1:
                    wasUpdated = _a.sent();
                    setIsSubmittingAccountDetails(false);
                    if (!wasUpdated) {
                        showToast("Unable to update account details.", "error");
                        return [2 /*return*/];
                    }
                    setIsAccountManagementOpen(false);
                    showToast("Account details updated.", "success");
                    return [2 /*return*/];
            }
        });
    }); };
    var handleSwitchToPickup = function () {
        if (!pickupServiceType) {
            showToast("Pickup service type is unavailable right now.", "error");
            return;
        }
        if (isChangingServiceType) {
            return;
        }
        setIsChangingServiceType(true);
        setCartServiceTypeAndRefresh(pickupServiceType, null)
            .then(function () {
            setMinimumDeliveryModalOpen(false);
        })
            .catch(function (error) {
            showServiceTypeUpdateErrorToast(error, "We couldn't switch to pickup right now.");
        })
            .finally(function () {
            setIsChangingServiceType(false);
        });
    };
    var handleSetAsapOrderAndRefresh = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            if (isChangingServiceType) {
                return [2 /*return*/];
            }
            return [2 /*return*/, setAsapOrderAndRefresh()];
        });
    }); };
    var handleSetFutureOrderAndRefresh = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (isChangingServiceType) {
                    return [2 /*return*/];
                }
                return [2 /*return*/, setFutureOrderAndRefresh.apply(void 0, args)];
            });
        });
    };
    var handleSelectedFutureDateAndRefresh = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (isChangingServiceType) {
                    return [2 /*return*/];
                }
                return [2 /*return*/, setSelectedFutureDateAndRefresh.apply(void 0, args)];
            });
        });
    };
    var handleValidateFutureOrderDatetime = function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (isChangingServiceType) {
                    return [2 /*return*/];
                }
                return [2 /*return*/, validateFutureOrderDatetime.apply(void 0, args)];
            });
        });
    };
    var isCheckoutLoading = serviceTypesAreLoading || cartIsLoading;
    if (isCheckoutLoading) {
        return (React.createElement(StateInitializationLoading, { label: "Loading checkout details", wrapperClassName: "vertical-checkout-page", mainClassName: "vertical-checkout", sectionClassName: "vertical-checkout-section", ariaLabel: "Checkout details" }));
    }
    var handleFinalizeCashOrder = function () { return __awaiter(void 0, void 0, void 0, function () {
        var isCommentValid, isSmsNumberValid, cartId, resolvedUserId, trimmedComment, trimmedSmsNumber, trimmedUserPhone, finalizeResponse, error_4;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (isSubmittingCashOrder) {
                        return [2 /*return*/];
                    }
                    if (!canSelectPayment) {
                        setCashOrderErrorMessage(PAYMENT_SELECTION_REQUIREMENTS_MESSAGE);
                        return [2 /*return*/];
                    }
                    if (!showOrderComments) return [3 /*break*/, 2];
                    return [4 /*yield*/, trigger("orderComment")];
                case 1:
                    isCommentValid = _c.sent();
                    if (!isCommentValid) {
                        return [2 /*return*/];
                    }
                    _c.label = 2;
                case 2:
                    if (!(shouldSendSmsAlerts || isCurbsideOrder)) return [3 /*break*/, 4];
                    return [4 /*yield*/, trigger("smsNumber")];
                case 3:
                    isSmsNumberValid = _c.sent();
                    if (!isSmsNumberValid) {
                        return [2 /*return*/];
                    }
                    _c.label = 4;
                case 4:
                    cartId = (_a = cart === null || cart === void 0 ? void 0 : cart.id) !== null && _a !== void 0 ? _a : null;
                    resolvedUserId = (_b = user === null || user === void 0 ? void 0 : user.id) !== null && _b !== void 0 ? _b : null;
                    trimmedComment = String(orderComment || "").trim();
                    trimmedSmsNumber = String(smsNumber || "").trim();
                    trimmedUserPhone = String((user === null || user === void 0 ? void 0 : user.phone) || "").trim();
                    setIsSubmittingCashOrder(true);
                    _c.label = 5;
                case 5:
                    _c.trys.push([5, 13, 14, 15]);
                    if (!(resolvedUserId && trimmedSmsNumber.length > 0 && trimmedSmsNumber !== trimmedUserPhone)) return [3 /*break*/, 7];
                    return [4 /*yield*/, updateUserProfile({
                            firstName: String((user === null || user === void 0 ? void 0 : user.first_name) || ""),
                            lastName: String((user === null || user === void 0 ? void 0 : user.last_name) || ""),
                            email: String((user === null || user === void 0 ? void 0 : user.email) || ""),
                            phone: trimmedSmsNumber,
                        })];
                case 6:
                    _c.sent();
                    _c.label = 7;
                case 7: return [4 /*yield*/, finalizeCashOrder({
                        comment: showOrderComments ? (trimmedComment.length > 0 ? trimmedComment : null) : undefined,
                        cartId: cartId,
                        userId: resolvedUserId,
                        shouldSendSmsAlerts: shouldSendSmsAlerts,
                        hasMarketingOptIn: hasPersistedMarketingOptIn || hasMarketingOptIn,
                        smsNumber: trimmedSmsNumber || null,
                        isCurbsideOrder: isCurbsideOrder,
                        signUpRewards: shouldSignUpRewards,
                    })];
                case 8:
                    finalizeResponse = _c.sent();
                    if (!finalizeResponse.errorMessage) return [3 /*break*/, 11];
                    setCashOrderErrorMessage(finalizeResponse.errorMessage);
                    if (!(finalizeResponse.errorType === "deferred_not_valid")) return [3 /*break*/, 10];
                    return [4 /*yield*/, getCart()];
                case 9:
                    _c.sent();
                    window.requestAnimationFrame(scrollToOrderTimeSelection);
                    _c.label = 10;
                case 10: return [2 /*return*/];
                case 11:
                    PersistenceManager.clearUserRewardsInfo();
                    return [4 /*yield*/, refreshUserHistory()];
                case 12:
                    _c.sent();
                    handleCreditOrderComplete(finalizeResponse.order);
                    return [3 /*break*/, 15];
                case 13:
                    error_4 = _c.sent();
                    setCashOrderErrorMessage(extractApiErrorMessage(error_4, "We couldn't place your order right now. Please try again."));
                    return [3 /*break*/, 15];
                case 14:
                    setIsSubmittingCashOrder(false);
                    return [7 /*endfinally*/];
                case 15: return [2 /*return*/];
            }
        });
    }); };
    if (cartLines.length === 0) {
        return (React.createElement("div", { className: "vertical-checkout-page" },
            React.createElement("main", { className: "vertical-checkout vertical-checkout--empty", "aria-label": "Checkout details" },
                React.createElement("section", { className: "vertical-checkout-section vertical-checkout-empty-cart", "aria-labelledby": "vertical-empty-cart-heading" },
                    React.createElement("h2", { id: "vertical-empty-cart-heading", className: "vertical-checkout-empty-cart__title" }, "Your cart is empty."),
                    React.createElement(RadixButton, { type: "button", className: "radix-submit-button vertical-checkout-empty-cart__cta mt-2", onClick: function () {
                            window.location.assign("".concat(window.relativeURI(), "/menu"));
                        } }, "View Menu")))));
    }
    if (isRedirectingAfterOrderSubmission) {
        return React.createElement(RadixLoading, { label: "Finalizing your order", fullScreen: true });
    }
    return (React.createElement("div", { className: "vertical-checkout-page" },
        React.createElement(MarketingDisplay, { placement: "checkout" }),
        React.createElement("main", { className: "vertical-checkout", "aria-label": "Checkout details" },
            React.createElement("section", { className: "vertical-checkout-section vertical-checkout-store-section", "aria-label": "Store information" },
                React.createElement("div", { className: "vertical-checkout-store-header" },
                    React.createElement("div", { className: "vertical-checkout-section-heading" },
                        React.createElement("span", { className: "section-icon", "aria-hidden": "true" },
                            React.createElement(StoreIcon, null)),
                        React.createElement("h2", { className: "vertical-checkout-heading" }, "Your Store"),
                        React.createElement("hr", { className: "section-divider" })),
                    React.createElement("div", { className: "vertical-checkout-store-content" },
                        React.createElement("p", { className: "vertical-checkout-copy vertical-checkout-focal-point" }, restaurantName),
                        React.createElement("p", { className: "vertical-checkout-copy" }, restaurantStreet),
                        React.createElement("p", { className: "vertical-checkout-copy" }, restaurantAddressLine),
                        React.createElement(StoreMapEmbed, { name: restaurantName, address: store === null || store === void 0 ? void 0 : store.address, city: store === null || store === void 0 ? void 0 : store.city, state: store === null || store === void 0 ? void 0 : store.state, zip: store === null || store === void 0 ? void 0 : store.zip })))),
            React.createElement("section", { className: "vertical-checkout-section", "aria-labelledby": "vertical-user-info-heading" },
                React.createElement("div", { className: "vertical-checkout-section-heading" },
                    React.createElement("span", { className: "section-icon", "aria-hidden": "true" },
                        React.createElement(PersonIcon, null)),
                    React.createElement("h2", { id: "vertical-user-info-heading", className: "vertical-checkout-heading" }, "Your Information")),
                hasCheckoutUser ? (React.createElement("div", { className: "vertical-checkout-user-details" },
                    React.createElement("div", { className: "vertical-checkout-user-name-row" },
                        React.createElement("p", { className: "vertical-checkout-copy vertical-checkout-focal-point" },
                            String((user === null || user === void 0 ? void 0 : user.first_name) || ""),
                            " ",
                            String((user === null || user === void 0 ? void 0 : user.last_name) || "")),
                        React.createElement(RadixButton, { type: "button", className: "vertical-checkout-user-edit-icon", onClick: function () { return setIsAccountManagementOpen(true); }, "aria-label": "Edit your information", title: "Edit" },
                            React.createElement(EditIconCheckbox, null))),
                    React.createElement("p", { className: "vertical-checkout-copy text-secondary" }, String((user === null || user === void 0 ? void 0 : user.email) || "")),
                    React.createElement("p", { className: "vertical-checkout-copy text-secondary" }, String((user === null || user === void 0 ? void 0 : user.phone) || "")),
                    storeUsesRewards && !isRewardsMember ?
                        React.createElement("div", { className: "vertical-checkout-rewards-callout" },
                            React.createElement("p", { className: "vertical-checkout-rewards-callout-title" }, "Rewards signup"),
                            React.createElement(RadixCheckbox, { checked: wantsRewardsSignup, onCheckedChange: setWantsRewardsSignup, label: rewardsSignupLabel }),
                            React.createElement("p", { className: "vertical-checkout-rewards-disclaimer" }, "By joining rewards, you agree to receive rewards-related messages.")) : null)) : (React.createElement("div", { className: "vertical-checkout-guest-form" },
                    React.createElement(Controller, { name: "guestFirstName", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixInput, { label: "First Name", id: "guest-first-name", value: field.value || "", onChange: function (event) {
                                    field.onChange(event.target.value);
                                    debouncedTrigger("guestFirstName");
                                } }));
                        } }),
                    errors.guestFirstName ?
                        React.createElement("p", { className: "vertical-checkout-error" }, errors.guestFirstName.message) : null,
                    React.createElement(Controller, { name: "guestLastName", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixInput, { label: "Last Name", id: "guest-last-name", value: field.value || "", onChange: function (event) {
                                    field.onChange(event.target.value);
                                    debouncedTrigger("guestLastName");
                                } }));
                        } }),
                    errors.guestLastName ?
                        React.createElement("p", { className: "vertical-checkout-error" }, errors.guestLastName.message) : null,
                    React.createElement(Controller, { name: "guestEmail", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixInput, { label: "Email", id: "guest-email", type: "email", value: field.value || "", onChange: function (event) {
                                    field.onChange(event.target.value);
                                    debouncedTrigger("guestEmail");
                                } }));
                        } }),
                    errors.guestEmail ?
                        React.createElement("p", { className: "vertical-checkout-error" }, errors.guestEmail.message) : null,
                    React.createElement(Controller, { name: "guestPhone", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixPhoneInput, { label: "Phone", id: "guest-phone", value: field.value || "", onValueChange: function (value) {
                                    field.onChange(value);
                                    debouncedTrigger("guestPhone");
                                } }));
                        } }),
                    errors.guestPhone ?
                        React.createElement("p", { className: "vertical-checkout-error" }, errors.guestPhone.message) : null,
                    React.createElement("p", { className: "vertical-checkout-copy mt-1" },
                        "Already have an account?",
                        " ",
                        React.createElement("button", { type: "button", className: "vertical-checkout-guest-signin-link", onClick: openSignInModal }, "Sign in")),
                    React.createElement("hr", { className: "hr-divider" }),
                    React.createElement("p", { className: "vertical-checkout-copy mb-2" }, "Add a password to enjoy faster checkout, previous order history, and more."),
                    React.createElement(Controller, { name: "guestPassword", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixInput, { label: "Password (Optional)", id: "guest-password", type: isGuestPasswordVisible ? "text" : "password", value: field.value || "", onChange: function (event) {
                                    field.onChange(event.target.value);
                                    debouncedTrigger("guestPassword");
                                }, rightElement: (React.createElement("button", { type: "button", className: "radix-input-icon-button", onClick: function () { return setIsGuestPasswordVisible(function (currentValue) { return !currentValue; }); }, "aria-label": isGuestPasswordVisible ? "Hide password" : "Show password" },
                                    React.createElement("i", { className: "fas ".concat(isGuestPasswordVisible ? "fa-eye-slash" : "fa-eye") }))) }));
                        } }),
                    hasGuestPassword ? (React.createElement(React.Fragment, null,
                        React.createElement(Controller, { name: "guestConfirmPassword", control: control, render: function (_a) {
                                var field = _a.field;
                                return (React.createElement(RadixInput, { label: "Confirm password", id: "guest-confirm-password", type: isGuestPasswordVisible ? "text" : "password", value: field.value || "", onChange: function (event) {
                                        field.onChange(event.target.value);
                                        debouncedTrigger("guestConfirmPassword");
                                    } }));
                            } }))) : null,
                    React.createElement("ul", { className: "vertical-checkout-password-requirements", "aria-live": "polite" }, guestPasswordRequirements.map(function (requirement) { return (React.createElement("li", { key: requirement.label, className: "vertical-checkout-password-requirement ".concat(requirement.isMet ? "vertical-checkout-password-requirement--met" : "vertical-checkout-password-requirement--unmet") },
                        React.createElement("span", { className: "vertical-checkout-password-requirement-icon", "aria-hidden": "true" }, requirement.isMet ? React.createElement(CheckCircledIcon, null) : React.createElement(CrossCircledIcon, null)),
                        React.createElement("span", { className: "vertical-checkout-password-requirement-label" }, requirement.label))); })),
                    storeUsesRewards ? (React.createElement("div", { className: "vertical-checkout-rewards-callout" },
                        React.createElement("p", { className: "vertical-checkout-rewards-callout-title" }, "Rewards signup"),
                        React.createElement(RadixCheckbox, { checked: wantsRewardsSignup, onCheckedChange: setWantsRewardsSignup, label: rewardsSignupLabel }),
                        React.createElement("p", { className: "vertical-checkout-rewards-disclaimer" }, "By joining rewards, you agree to receive rewards-related messages."))) : null,
                    React.createElement(SmsMarketingOptIn, { id: "vertical-checkout-guest-marketing-opt-in", checked: hasMarketingOptIn, onCheckedChange: setHasMarketingOptIn, disabled: isCreatingGuestUser }),
                    React.createElement(RadixButton, { type: "button", className: "vertical-checkout-guest-continue-button", onClick: handleCreateGuestUser, disabled: isGuestContinueDisabled }, isCreatingGuestUser ? "Saving..." : (hasGuestPassword ? "Create account and continue" : "Continue as guest"))))),
            hasCheckoutUser ? (React.createElement(React.Fragment, null,
                React.createElement("section", { className: "vertical-checkout-section", "aria-labelledby": "vertical-service-type-heading" },
                    React.createElement("div", { className: "vertical-checkout-section-heading" },
                        React.createElement("span", { className: "section-icon", "aria-hidden": "true" },
                            React.createElement(DeliveryServiceIcon, null)),
                        React.createElement("h2", { id: "vertical-service-type-heading", className: "vertical-checkout-heading" }, "Service Type")),
                    React.createElement("p", { className: "vertical-checkout-copy", "aria-live": "polite" },
                        "Selected: ",
                        String((activeServiceType === null || activeServiceType === void 0 ? void 0 : activeServiceType.name) || "")
                            || React.createElement("span", { className: "" }, "Please select a service type.")),
                    React.createElement(RadixToggleGroup, { options: serviceTypeOptions, value: selectedServiceTypeValue, onValueChange: handleServiceTypeChange, disabled: isChangingServiceType, ariaLabel: "Choose service type", className: "vertical-checkout-toggle-group" }),
                    selectedServiceTypeIsUnavailableForMenu ? (React.createElement("p", { className: "vertical-checkout-error", role: "status" }, serviceTypeUnavailableForMenuMessage)) : null,
                    isCateringOrder && !minimumPickupMet ? (React.createElement("p", { className: "vertical-checkout-error", role: "status" },
                        "Pickup catering orders require a minimum subtotal of ",
                        formatCurrency(cateringMinimumPickupAmount),
                        ". Your current subtotal is ",
                        formatCurrency(cartSubtotal),
                        ".")) : null,
                    !minimumDeliveryMet ? (React.createElement("p", { className: "vertical-checkout-error", role: "status" },
                        "Delivery requires a minimum subtotal of ",
                        formatCurrency(minimumDeliveryAmount),
                        ". Your current subtotal is ",
                        formatCurrency(cartSubtotal),
                        ".")) : null,
                    isDeliverySelected ? (React.createElement("div", { className: "vertical-checkout-delivery-address-row", "aria-live": "polite" },
                        React.createElement("p", { className: "vertical-checkout-copy" },
                            "Delivering to: ",
                            selectedDeliveryAddress
                                ? (React.createElement(React.Fragment, null,
                                    React.createElement("br", null),
                                    selectedDeliveryAddressLines.lineOne,
                                    selectedDeliveryAddressLines.lineTwo ? React.createElement(React.Fragment, null,
                                        React.createElement("br", null),
                                        selectedDeliveryAddressLines.lineTwo) : null))
                                : "No address selected"),
                        React.createElement(RadixButton, { type: "button", className: "vertical-checkout-delivery-address-change", onClick: handleDeliveryAddressChangeClick }, "Change this"))) : null),
                isDeliverySelected && showOrderComments ? (React.createElement("section", { className: "vertical-checkout-section", "aria-labelledby": "vertical-delivery-comment-heading" },
                    React.createElement("div", { className: "vertical-checkout-section-heading", style: { marginBottom: 5 } },
                        React.createElement("span", { className: "section-icon", "aria-hidden": "true" },
                            React.createElement(ChatBubbleIcon, null)),
                        React.createElement("h2", { id: "vertical-delivery-comment-heading", className: "vertical-checkout-heading" }, "Delivery Comment")),
                    React.createElement(Controller, { name: "orderComment", control: control, render: function (_a) {
                            var field = _a.field;
                            return (React.createElement(RadixInput, { id: "vertical-checkout-order-comment", label: "We\u2019ll do our best to accommodate requests when we can.", type: "text", value: field.value || "", onChange: function (event) { return field.onChange(event.target.value); }, onBlur: field.onBlur, "aria-invalid": errors.orderComment ? "true" : "false", "aria-describedby": errors.orderComment ? "vertical-checkout-order-comment-error" : undefined, placeholder: "Enter a Comment (Max 40 Characters)", maxLength: 40 }));
                        } }),
                    errors.orderComment ? (React.createElement("p", { id: "vertical-checkout-order-comment-error", className: "vertical-checkout-error", role: "alert" }, errors.orderComment.message)) : null)) : null,
                React.createElement("section", { ref: orderTimeSectionRef, className: ["vertical-checkout-section", !hasServiceTypeSelected ? "vertical-checkout-section--disabled" : ""].filter(Boolean).join(" "), "aria-labelledby": "vertical-order-time-heading", "aria-disabled": !hasServiceTypeSelected },
                    React.createElement("div", { className: "vertical-checkout-section-heading vertical-checkout-section-heading--tight" },
                        React.createElement("span", { className: "section-icon", "aria-hidden": "true" },
                            React.createElement(ClockIcon, null)),
                        React.createElement("h2", { id: "vertical-order-time-heading", className: "vertical-checkout-heading", tabIndex: -1 }, "Order Time")),
                    effectiveOrderTiming === "future" ? (React.createElement("p", { className: "vertical-checkout-copy", style: { marginTop: "0.25rem" } }, "Scheduled at: ".concat(formatScheduledDatetimeDisplay(effectiveFutureOrderDatetime)))) : null,
                    React.createElement(OrderTimeSelectionFields, { orderTiming: effectiveOrderTiming, futureOrderDatetime: effectiveFutureOrderDatetime, onSetAsapOrder: handleSetAsapOrderAndRefresh, onSetFutureOrder: handleSetFutureOrderAndRefresh, hasFutureOrdering: hasFutureOrdering, futureDates: futureDates, selectedFutureDate: selectedFutureDate, futureDateHours: futureDateHours, futureAvailabilityMessage: futureAvailabilityMessage, futureAvailabilityLoading: futureAvailabilityLoading, onSelectFutureDate: handleSelectedFutureDateAndRefresh, onValidateFutureDatetime: handleValidateFutureOrderDatetime, canSelectAsap: !isChangingServiceType && hasServiceTypeSelected && serviceTypeEligibility.canAsap, canSelectFuture: !isChangingServiceType && hasServiceTypeSelected && serviceTypeEligibility.canFuture, disabledMessage: isChangingServiceType ? "Updating service type..." : (hasServiceTypeSelected ? null : "Choose a service type before selecting an order time."), onSelectionSuccess: function (timing) {
                            if (timing === "future") {
                                showToast("Future order time applied.", "success");
                                return;
                            }
                            showToast("Order time set to ASAP.", "success");
                        }, containerClassName: "order-time-selection-flyout--inline mt-2" })),
                React.createElement("section", { hidden: !showOrderPreferences, className: "vertical-checkout-section", "aria-labelledby": "vertical-curbside-heading" },
                    React.createElement("div", { className: "vertical-checkout-section-heading" },
                        React.createElement("span", { className: "section-icon", "aria-hidden": "true" },
                            React.createElement(MixerHorizontalIcon, null)),
                        React.createElement("h2", { id: "vertical-curbside-heading", className: "vertical-checkout-heading" }, "Order Preferences")),
                    (store === null || store === void 0 ? void 0 : store.has_curbside) ?
                        React.createElement(RadixCheckbox, { checked: isCurbsideOrder, onCheckedChange: handleCurbsideOrderChange, label: "".concat(store.curbside_title) }) : null,
                    Boolean(Number(store === null || store === void 0 ? void 0 : store.has_inotify)) ? (React.createElement(React.Fragment, null,
                        React.createElement(RadixCheckbox, { checked: shouldSendSmsAlerts, onCheckedChange: handleSmsAlertsChange, label: "Get Order Updates via SMS" }),
                        React.createElement(Controller, { name: "smsNumber", control: control, render: function (_a) {
                                var field = _a.field;
                                return (React.createElement(RadixPhoneInput, { id: "vertical-checkout-sms-number", label: "SMS phone number", disabled: !isSmsNumberInputEnabled, value: field.value || "", onValueChange: function (value) {
                                        field.onChange(value);
                                        if (isSmsNumberInputEnabled) {
                                            debouncedTrigger("smsNumber");
                                        }
                                    }, onBlur: field.onBlur, "aria-invalid": isSmsNumberInputEnabled && errors.smsNumber ? "true" : "false", "aria-describedby": isSmsNumberInputEnabled && errors.smsNumber ? "vertical-checkout-sms-number-error" : undefined, placeholder: "XXX-XXX-XXXX" }));
                            } }),
                        isSmsNumberInputEnabled && errors.smsNumber ? (React.createElement("p", { id: "vertical-checkout-sms-number-error", className: "vertical-checkout-error", role: "alert" }, errors.smsNumber.message)) : null)) : null,
                    showMarketingOptInInOrderPreferences ? (React.createElement(SmsMarketingOptIn, { id: "vertical-checkout-marketing-opt-in", checked: hasMarketingOptIn, onCheckedChange: setHasMarketingOptIn })) : null),
                React.createElement("section", { className: "vertical-checkout-section", "aria-labelledby": "vertical-order-details-heading" },
                    React.createElement(RadixAccordion, { type: "single", collapsible: true, className: [
                            "vertical-checkout-accordion",
                            isOrderDetailsExpanded
                                ? "vertical-checkout-accordion--expanded"
                                : "vertical-checkout-accordion--collapsed",
                        ].join(" "), value: isOrderDetailsExpanded ? "details" : "", onValueChange: function (value) {
                            setIsOrderDetailsExpanded(value === "details");
                        } },
                        React.createElement(RadixAccordionItem, { value: "details", className: "vertical-checkout-accordion-item" },
                            React.createElement(RadixAccordionHeader, { className: "vertical-checkout-accordion-header" },
                                React.createElement("div", null,
                                    React.createElement("div", { className: "vertical-checkout-section-heading vertical-checkout-section-heading--tight" },
                                        React.createElement("span", { className: "section-icon", "aria-hidden": "true" },
                                            React.createElement(ListBulletIcon, null)),
                                        React.createElement("h2", { id: "vertical-order-details-heading", className: "vertical-checkout-heading" },
                                            "Order Details (",
                                            itemCount,
                                            " item",
                                            itemCount === 1 ? "" : "s",
                                            ")"))),
                                React.createElement(RadixAccordionTrigger, { className: "vertical-checkout-accordion-trigger vertical-checkout-chevron-button" },
                                    React.createElement("span", { className: "vertical-checkout-accordion-trigger-icon", "aria-hidden": "true" }, isOrderDetailsExpanded ? React.createElement(ChevronDownIcon, null) : React.createElement(ChevronRightIcon, null)),
                                    React.createElement("span", { className: "vertical-checkout-sr-only" }, isOrderDetailsExpanded ? "Collapse order details" : "Expand order details"))),
                            React.createElement(RadixAccordionContent, { className: "vertical-checkout-accordion-content" },
                                React.createElement("div", { className: "vertical-checkout-accordion-inner" },
                                    React.createElement("ul", { className: "vertical-checkout-line-list vertical-checkout-order-details", "aria-label": "Order item details" }, cartLines.map(function (line, index) {
                                        var _a;
                                        var name = String(line.friendly_name || line.name || "Menu item");
                                        var quantity = typeof line.quantity === "number" ? line.quantity : 0;
                                        var lineTotal = toNumber((_a = (line.subtotal * line.quantity)) !== null && _a !== void 0 ? _a : 0);
                                        var wholeMods = getModifiersByPortion(line.modifications, ["whole"]);
                                        var firstHalfMods = getModifiersByPortion(line.modifications, ["first-half"]);
                                        var secondHalfMods = getModifiersByPortion(line.modifications, ["second-half"]);
                                        var unportionedMods = getItemModifiers(line.modifications).filter(function (modifier) {
                                            return !modifier.portion;
                                        });
                                        var lineComment = getLineComment(line.comment);
                                        var key = line.id || "".concat(name, "-").concat(index);
                                        return (React.createElement("li", { key: key, className: "vertical-checkout-line-item" },
                                            React.createElement("div", null,
                                                React.createElement("div", { className: "vertical-checkout-line-title-row" },
                                                    React.createElement("p", { className: "vertical-checkout-line-name" }, name),
                                                    !(Boolean(line.addon_id) || Boolean(Number(line.is_addon || 0)) || isSpecialDiscountLine(line)) ? (React.createElement(RadixButton, { type: "button", className: "vertical-checkout-order-line-edit-icon", onClick: function () {
                                                            void handleOrderLineEdit(line);
                                                        }, "aria-label": "Edit ".concat(name), disabled: Boolean(editingItem) },
                                                        React.createElement(EditIconCheckbox, null))) : null),
                                                React.createElement("p", { className: "vertical-checkout-line-meta" },
                                                    "Qty: ",
                                                    quantity),
                                                (wholeMods.length > 0 || firstHalfMods.length > 0 || secondHalfMods.length > 0 || unportionedMods.length > 0) ? (React.createElement("div", { className: "vertical-checkout-modifiers", "aria-label": "".concat(name, " modifiers") },
                                                    wholeMods.length > 0 ? (React.createElement("p", { className: "vertical-checkout-line-meta" },
                                                        React.createElement("strong", null, "Whole:"),
                                                        " ",
                                                        formatModifiers(wholeMods))) : null,
                                                    firstHalfMods.length > 0 ? (React.createElement("p", { className: "vertical-checkout-line-meta" },
                                                        React.createElement("strong", null, "First Half:"),
                                                        " ",
                                                        formatModifiers(firstHalfMods))) : null,
                                                    secondHalfMods.length > 0 ? (React.createElement("p", { className: "vertical-checkout-line-meta" },
                                                        React.createElement("strong", null, "Second Half:"),
                                                        " ",
                                                        formatModifiers(secondHalfMods))) : null,
                                                    unportionedMods.length > 0 ? (React.createElement("p", { className: "vertical-checkout-line-meta" }, formatModifiers(unportionedMods))) : null)) : null,
                                                lineComment ? React.createElement("p", { className: "vertical-checkout-line-comment" },
                                                    React.createElement("strong", null, "Note:"),
                                                    " ",
                                                    lineComment) : null),
                                            React.createElement("p", { className: "vertical-checkout-line-total" }, formatCurrency(lineTotal))));
                                    })),
                                    React.createElement(OrderSummary, { ncaFeeDescription: getXplorPayDebitFeeDescriptor(store === null || store === void 0 ? void 0 : store.credit_processor, store === null || store === void 0 ? void 0 : store.nca_title) })))))),
                React.createElement("section", { className: "vertical-checkout-section", "aria-labelledby": "vertical-tip-heading", hidden: !(serviceType === null || serviceType === void 0 ? void 0 : serviceType.allow_tip_selection) },
                    React.createElement("div", { className: "vertical-checkout-section-heading" },
                        React.createElement("span", { className: "section-icon", "aria-hidden": "true" },
                            React.createElement(GratuityIcon, null)),
                        React.createElement("h2", { id: "vertical-tip-heading", className: "vertical-checkout-heading" }, "Tip")),
                    React.createElement(RadixToggleGroup, { options: __spreadArray(__spreadArray([], tipPercentages.map(function (percentage) { return ({
                            value: String(percentage),
                            label: (React.createElement(React.Fragment, null,
                                React.createElement("span", { className: "vertical-checkout-tip-bold" }, "".concat(percentage, "%")),
                                React.createElement("br", null),
                                React.createElement("span", null, formatCurrency(cartSubtotal * (percentage / 100))))),
                            ariaLabel: "Add ".concat(percentage, " percent tip"),
                        }); }), true), [
                            {
                                value: "custom",
                                label: (React.createElement(React.Fragment, null,
                                    React.createElement("span", { className: "vertical-checkout-tip-bold" }, "Custom"),
                                    React.createElement("br", null),
                                    React.createElement("span", { className: "vertical-checkout-tip-bold" }, "Tip"))),
                                ariaLabel: "Add custom tip",
                            },
                        ], false), value: selectedTipOption, onValueChange: handleTipOptionChange, ariaLabel: "Choose tip percentage", className: "vertical-checkout-toggle-group vertical-checkout-tip-toggle-group", optionClassName: "vertical-checkout-tip-option", activeOptionClassName: "vertical-checkout-tip-option vertical-checkout-tip-option--active" }),
                    selectedTipOption === "custom" ? (React.createElement(React.Fragment, null,
                        React.createElement("label", { htmlFor: "vertical-checkout-manual-tip", className: "vertical-checkout-manual-tip-label" }, "Enter Tip Amount"),
                        React.createElement(Controller, { name: "manualTipInput", control: control, render: function (_a) {
                                var field = _a.field;
                                return (React.createElement(RadixInput, { id: "vertical-checkout-manual-tip", type: "text", inputMode: "decimal", value: field.value || "", onChange: function (event) { return handleManualTipChange(event.target.value); }, onBlur: function () {
                                        field.onBlur();
                                        handleManualTipBlur(field.value || "");
                                    }, "aria-invalid": errors.manualTipInput ? "true" : "false", "aria-describedby": errors.manualTipInput ? "vertical-checkout-tip-error" : undefined, placeholder: "Enter tip amount" }));
                            } }),
                        errors.manualTipInput ? (React.createElement("p", { id: "vertical-checkout-tip-error", className: "vertical-checkout-error", role: "alert" }, errors.manualTipInput.message)) : null)) : null,
                    React.createElement("p", { className: "vertical-checkout-copy", "aria-live": "polite" },
                        "Tip Amount: ",
                        selectedTip === null ? formatCurrency(0) : formatCurrency(selectedTip),
                        isTipUpdating ? " (updating...)" : "")),
                React.createElement(DiscountsSection, { offers: (history === null || history === void 0 ? void 0 : history.offers) || [], appliedCodes: appliedDiscountCodes, initialManualCode: pendingDealCode, onApplyDiscount: handleApplyDiscount, onRemoveDiscount: handleRemoveDiscount }),
                React.createElement("section", { ref: paymentSectionRef, className: "vertical-checkout-section", "aria-labelledby": "vertical-payment-heading" },
                    React.createElement("div", { className: "vertical-checkout-payment-header" },
                        React.createElement("div", { className: "vertical-checkout-section-heading vertical-checkout-section-heading--tight" },
                            React.createElement("span", { className: "section-icon", "aria-hidden": "true" },
                                React.createElement(CreditCardGlyph, null)),
                            React.createElement("h2", { ref: paymentHeadingRef, id: "vertical-payment-heading", className: "vertical-checkout-heading", tabIndex: -1 }, "Payment")),
                        showCardTypeBadges ? (React.createElement("div", { className: "vertical-checkout-payment-badges", "aria-hidden": "true" },
                            React.createElement("span", { className: "vertical-checkout-payment-badge" }, "VISA"),
                            React.createElement("span", { className: "vertical-checkout-payment-badge" }, "AMEX"),
                            React.createElement("span", { className: "vertical-checkout-payment-badge" }, "DISC"),
                            React.createElement("span", { className: "vertical-checkout-payment-badge" }, "MC"))) : null),
                    cateringPaymentMustCallStore ? (React.createElement("p", { className: "vertical-checkout-error", role: "status" }, "This catering order must be paid online. Online payment is not available for the selected service type, so please call the store to place your order.")) : null,
                    cateringRequiresPrePayment && creditPaymentsEnabled ? (React.createElement("p", { className: "vertical-checkout-copy", role: "status" }, "This catering order must be paid online. Cash will not be accepted for this service type.")) : null,
                    React.createElement(CreditPaymentContextProvider, { tip: selectedTip !== null && selectedTip !== void 0 ? selectedTip : 0, handle_credit_order_complete: handleCreditOrderComplete, hidden: !canSelectPayment, checkoutComment: showOrderComments ? String(orderComment || "").trim() || null : null, showPaymentForm: paymentType === "credit"
                            || paymentType === "apple_pay"
                            || paymentType === "google_pay", selectedPaymentType: paymentType, onExit: function () {
                            setPaymentType("none");
                        }, signUpRewards: shouldSignUpRewards },
                        React.createElement(PaymentOptionsWithSavedCards, { canSelectPayment: canSelectPayment, creditPaymentsEnabled: creditPaymentsEnabled, paymentType: paymentType, setPaymentType: handlePaymentTypeChange, paymentOptions: paymentOptions, payLaterTabLabel: isDeliverySelected ? "Pay at the Door" : "Pay at the Restaurant", onFinalizeCashOrder: function () {
                                void handleFinalizeCashOrder();
                            }, isSubmittingCashOrder: isSubmittingCashOrder }))))) : null),
        React.createElement(UserAccountManagementFlyOut, { open: isAccountManagementOpen, onOpenChange: setIsAccountManagementOpen, onSubmit: handleAccountDetailsUpdate, defaultValues: accountManagementDefaultValues, isSubmitting: isSubmittingAccountDetails, onDeleteAccount: function () {
            }, isDeletingAccount: false, showDeleteAccountSection: false }),
        React.createElement(OrderAddressDetailsFlyOut, { open: orderAddressDetailsFlyoutIsOpen, onOpenChange: handleAddressFlyOutOpenChange, address: currentAddress || inferredDeliveryAddressRecord, onApplyAddress: handleAddressApply, previousAddresses: (history === null || history === void 0 ? void 0 : history.addresses) || [] }),
        React.createElement(RadixModal, { open: Boolean(cashOrderErrorMessage), onOpenChange: function (open) {
                if (!open) {
                    setCashOrderErrorMessage(null);
                }
            }, title: "Unable to place order", description: cashOrderErrorMessage || "We couldn't place your order right now. Please try again.", status: "error" }),
        React.createElement(RadixModal, { open: minimumDeliveryModalOpen, onOpenChange: setMinimumDeliveryModalOpen, title: "Order minimum not met", description: "".concat(selectedServiceTypeIsDelivery ? "Delivery" : "Pickup", " requires a minimum subtotal of ").concat(formatCurrency(selectedServiceTypeMinimumAmount), ". Your current subtotal is ").concat(formatCurrency(cartSubtotal), "."), status: "info" })));
};
